/**
 * Gift System Manager
 * Sistema de Brindes para Shopify Theme
 *
 * @version 1.0.0
 * @author LFpro Team
 */

/**
 * Progresso composto do brinde.
 * Valor preenche até valueShare%; o produto gatilho completa o restante até 100%.
 * Sem produtos gatilho no nível, só o valor conta (0–100%).
 * A fatia do gatilho enche proporcionalmente à quantidade atingida quando
 * options.triggerProgress (saída de resolveTriggerProgress do núcleo, com
 * required/matched/satisfied) é informado. Sem ele, cai na forma booleana
 * anterior (options.hasTrigger) e produz exatamente o resultado de antes,
 * porque existe chamada em produção que ainda passa só o booleano.
 * Exportado em window para prova/debug sem duplicar a regra.
 *
 * @param {number} cartTotal - Total do carrinho na mesma unidade de minValue (reais)
 * @param {number} minValue - Threshold do nível
 * @param {{ needsTrigger?: boolean, hasTrigger?: boolean, triggerProgress?: object, valueShare?: number, triggerShare?: number }} [options]
 * @returns {number} 0–100
 */
function computeGiftProgressPercent(cartTotal, minValue, options) {
  const opts = options || {};
  const valueShare = typeof opts.valueShare === 'number' ? opts.valueShare : 80;
  const triggerShare = typeof opts.triggerShare === 'number' ? opts.triggerShare : 20;
  const needsTrigger = Boolean(opts.needsTrigger);

  if (!minValue || minValue <= 0) {
    return 0;
  }

  const valueRatio = Math.min(Math.max(Number(cartTotal) / minValue, 0), 1);

  if (!needsTrigger) {
    return Math.min(100, Math.round(valueRatio * 100));
  }

  const progress = opts.triggerProgress;
  let triggerRatio;
  if (progress && typeof progress.matched === 'number' && progress.required > 0) {
    triggerRatio = Math.min(Math.max(progress.matched / progress.required, 0), 1);
  } else if (progress) {
    triggerRatio = progress.satisfied ? 1 : 0;
  } else {
    triggerRatio = opts.hasTrigger ? 1 : 0;
  }

  return Math.min(
    100,
    Math.round(valueRatio * valueShare + triggerRatio * triggerShare)
  );
}

if (typeof window !== 'undefined') {
  window.computeGiftProgressPercent = computeGiftProgressPercent;
}

/**
 * Resolve a copy da barra por estado.
 * - progress: ainda falta valor (pode usar {valor}/{nivel})
 * - trigger_missing: valor ok, falta produto gatilho (NÃO deve falar em R$ faltando)
 * - unlocked: desbloqueado
 *
 * Defesa: se o setting de trigger_missing for texto de dinheiro (erro comum no editor),
 * cai para messageProgress (sem {valor}) ou fallback de produto.
 */
function resolveGiftProgressMessage(state, config, options) {
  const opts = options || {};
  const remaining = typeof opts.remaining === 'number' ? opts.remaining : 0;
  const missing = typeof opts.missing === 'number' ? opts.missing : 0;
  const levelNumber = opts.levelNumber != null ? opts.levelNumber : '';
  const formatCurrency = typeof opts.formatCurrency === 'function'
    ? opts.formatCurrency
    : (v) => String(v);

  const fill = (template) => String(template || '')
    .replace(/\{valor\}/g, formatCurrency(Math.max(remaining, 0)))
    .replace(/\{nivel\}/g, String(levelNumber))
    .replace(/\{faltam\}/g, String(Math.max(missing, 0)));

  if (state === 'unlocked') {
    return fill(config && config.messageUnlockedSingle
      ? config.messageUnlockedSingle
      : '🎁 Parabéns! Você ganhou um brinde!');
  }

  if (state === 'trigger_missing') {
    const productFallback = 'Adicione um dos produtos especiais para desbloquear o brinde!';
    let template = (config && config.messageTriggerMissing) || productFallback;

    // Template com {faltam} fala de quantidade de produto, não de dinheiro:
    // nunca é trocado pela heurística abaixo, porque {faltam} é exatamente o
    // marcador que este estado existe para preencher (valor já atingido,
    // falta gatilho) — o momento em que a heurística de "R$" dispararia hoje.
    const hasQuantityMarker = /\{faltam\}/i.test(template);

    // Mensagem de "falta R$" com valor já atingido = setting no campo errado
    const looksLikeMoneyLeft = !hasQuantityMarker && (
      /\{valor\}/i.test(template) ||
      /falta\s+(apenas\s+)?r\s*\$/i.test(template) ||
      (/falta\s+apenas/i.test(template) && /r\$|reais|valor/i.test(template))
    );

    if (looksLikeMoneyLeft && remaining <= 0) {
      const progressMsg = (config && config.messageProgress) || '';
      if (progressMsg && !/\{valor\}/i.test(progressMsg)) {
        template = progressMsg;
      } else {
        template = productFallback;
      }
    }

    return fill(template);
  }

  // progress (falta valor)
  return fill(
    (config && config.messageProgress) || 'Falta apenas {valor} para ganhar um brinde!'
  );
}

if (typeof window !== 'undefined') {
  window.resolveGiftProgressMessage = resolveGiftProgressMessage;
}

(function giftSystemRuntimeScope() {
'use strict';

const GiftSystemRuntime = typeof window === 'undefined' ? globalThis : window;
const GiftSystemElementBase = typeof HTMLElement === 'undefined'
  ? class {}
  : HTMLElement;

const GiftChoiceCoreDependency = typeof module === 'object' && module.exports
  ? require('./gift-choice-core.js')
  : null;

function cloneGiftChoiceValue(value) {
  if (value == null) {
    return value;
  }

  return JSON.parse(JSON.stringify(value));
}

function isGiftChoiceRecord(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function sanitizeDecisionSnapshot(value) {
  if (!isGiftChoiceRecord(value)) {
    return {};
  }

  const snapshot = {};

  Object.keys(value).forEach((cartToken) => {
    const levels = value[cartToken];

    if (!isGiftChoiceRecord(levels)) {
      return;
    }

    const sanitizedLevels = {};

    Object.keys(levels).forEach((levelNumber) => {
      const entry = levels[levelNumber];

      if (
        isGiftChoiceRecord(entry) &&
        entry.decision === 'none' &&
        typeof entry.signature === 'string'
      ) {
        sanitizedLevels[String(levelNumber)] = {
          decision: 'none',
          signature: entry.signature,
        };
      }
    });

    if (Object.keys(sanitizedLevels).length > 0) {
      snapshot[cartToken] = sanitizedLevels;
    }
  });

  return snapshot;
}

class GiftDecisionStorageAdapter {
  constructor({
    storage,
    memory = new Map(),
    core,
    namespace = core && core.DECISION_STORAGE_KEY,
  }) {
    if (
      !core ||
      typeof core.getNoneDecision !== 'function' ||
      typeof core.recordNoneDecision !== 'function' ||
      typeof core.clearNoneDecision !== 'function'
    ) {
      throw new TypeError('GiftChoiceCore inválido');
    }

    this.storage = storage || null;
    this.memory = memory instanceof Map ? memory : new Map();
    this.core = core;
    this.namespace = namespace || core.DECISION_STORAGE_KEY;
    this.lastSnapshot = {};
    this.currentMode = this.storage ? 'storage' : 'memory';

    if (this.currentMode === 'memory' && !this.memory.has(this.namespace)) {
      this.memory.set(this.namespace, {});
    }
  }

  get mode() {
    return this.currentMode;
  }

  getNone(scope) {
    return this.core.getNoneDecision(this.readSnapshot(), scope);
  }

  setNone(scope) {
    if (
      !isGiftChoiceRecord(scope) ||
      (scope.decision !== undefined && scope.decision !== 'none')
    ) {
      return null;
    }

    const current = this.readSnapshot();
    const next = this.core.recordNoneDecision(current, scope);

    this.writeSnapshot(next, current);
    return this.core.getNoneDecision(next, scope);
  }

  clearLevel(scope) {
    const current = this.readSnapshot();
    const next = this.core.clearNoneDecision(current, scope);

    this.writeSnapshot(next, current);
    return next;
  }

  retainOnlyCart(cartToken) {
    const current = this.readSnapshot();
    const next = (
      typeof cartToken === 'string' &&
      cartToken.length > 0 &&
      Object.prototype.hasOwnProperty.call(current, cartToken)
    )
      ? { [cartToken]: cloneGiftChoiceValue(current[cartToken]) }
      : {};

    this.writeSnapshot(next, current);
    return next;
  }

  clearAll() {
    if (this.currentMode === 'memory') {
      this.memory.delete(this.namespace);
      this.lastSnapshot = {};
      return {};
    }

    try {
      this.storage.removeItem(this.namespace);
      this.lastSnapshot = {};
      this.memory.delete(this.namespace);
    } catch (error) {
      this.activateMemory(this.lastSnapshot);
      this.memory.delete(this.namespace);
      this.lastSnapshot = {};
    }

    return {};
  }

  readSnapshot() {
    if (this.currentMode === 'memory') {
      const snapshot = sanitizeDecisionSnapshot(
        this.memory.get(this.namespace) || {},
      );
      this.lastSnapshot = cloneGiftChoiceValue(snapshot);
      return cloneGiftChoiceValue(snapshot);
    }

    try {
      const rawValue = this.storage.getItem(this.namespace);
      let parsed = {};

      if (typeof rawValue === 'string' && rawValue.length > 0) {
        try {
          parsed = JSON.parse(rawValue);
        } catch (error) {
          parsed = {};
        }
      }

      const snapshot = sanitizeDecisionSnapshot(parsed);
      this.lastSnapshot = cloneGiftChoiceValue(snapshot);
      return cloneGiftChoiceValue(snapshot);
    } catch (error) {
      this.activateMemory(this.lastSnapshot);
      return this.readSnapshot();
    }
  }

  writeSnapshot(value, fallbackSnapshot = this.lastSnapshot) {
    const snapshot = sanitizeDecisionSnapshot(value);

    if (this.currentMode === 'memory') {
      this.memory.set(this.namespace, cloneGiftChoiceValue(snapshot));
      this.lastSnapshot = cloneGiftChoiceValue(snapshot);
      return cloneGiftChoiceValue(snapshot);
    }

    try {
      this.storage.setItem(this.namespace, JSON.stringify(snapshot));
      this.lastSnapshot = cloneGiftChoiceValue(snapshot);
    } catch (error) {
      this.activateMemory(fallbackSnapshot);
      this.memory.set(this.namespace, cloneGiftChoiceValue(snapshot));
      this.lastSnapshot = cloneGiftChoiceValue(snapshot);
    }

    return cloneGiftChoiceValue(snapshot);
  }

  activateMemory(snapshot) {
    if (this.currentMode === 'memory') {
      return;
    }

    const safeSnapshot = sanitizeDecisionSnapshot(snapshot);

    this.currentMode = 'memory';
    this.memory.set(this.namespace, cloneGiftChoiceValue(safeSnapshot));
    this.lastSnapshot = cloneGiftChoiceValue(safeSnapshot);
  }
}

function normalizeGiftChoiceLevelNumber(value) {
  const levelNumber = Number(value);

  if (
    !Number.isFinite(levelNumber) ||
    !Number.isInteger(levelNumber) ||
    levelNumber <= 0
  ) {
    return null;
  }

  return levelNumber;
}

function isGiftChoiceLine(item) {
  return (
    isGiftChoiceRecord(item) &&
    item.quantity > 0 &&
    isGiftChoiceRecord(item.properties) &&
    item.properties._is_gift === 'true'
  );
}

function getGiftChoiceLineLevel(item) {
  if (!isGiftChoiceLine(item)) {
    return null;
  }

  return normalizeGiftChoiceLevelNumber(item.properties._gift_level);
}

function normalizeResolvedGiftChoiceLevel(level) {
  if (
    !isGiftChoiceRecord(level) ||
    !Object.prototype.hasOwnProperty.call(level, 'status') ||
    typeof level.eligible !== 'boolean' ||
    typeof level.signature !== 'string' ||
    !Array.isArray(level.viableOptions) ||
    !Array.isArray(level.giftLines)
  ) {
    return null;
  }

  const levelNumber = normalizeGiftChoiceLevelNumber(level.levelNumber);

  if (levelNumber === null) {
    return null;
  }

  return {
    ...cloneGiftChoiceValue(level),
    levelNumber,
    eligible: level.eligible,
    status: level.status,
    signature: level.signature,
    viableOptions: cloneGiftChoiceValue(level.viableOptions),
    giftLines: cloneGiftChoiceValue(level.giftLines),
  };
}

function isConfirmedGiftCart(value) {
  return (
    isGiftChoiceRecord(value) &&
    typeof value.token === 'string' &&
    value.token.length > 0 &&
    Array.isArray(value.items)
  );
}

function hasGiftCartItems(value) {
  return isGiftChoiceRecord(value) && Array.isArray(value.items);
}

function createGiftCartError(code) {
  const error = new Error(code);

  error.code = code;
  return error;
}

function normalizeGiftHandle(value) {
  return typeof value === 'string'
    ? value.trim().toLowerCase()
    : '';
}

function joinGiftRouteRoot(root, relativePath) {
  const normalizedRoot = typeof root === 'string'
    ? root.trim().replace(/\/+$/u, '')
    : '';
  const normalizedPath = String(relativePath || '').replace(/^\/+/u, '');

  return normalizedRoot
    ? `${normalizedRoot}/${normalizedPath}`
    : `/${normalizedPath}`;
}

function ensureGiftJsonSuffix(route) {
  if (typeof route !== 'string' || route.trim().length === 0) {
    return '';
  }

  const normalized = route.trim();
  const queryIndex = normalized.indexOf('?');
  const path = queryIndex === -1 ? normalized : normalized.slice(0, queryIndex);
  const query = queryIndex === -1 ? '' : normalized.slice(queryIndex);

  return `${path.endsWith('.js') ? path : `${path}.js`}${query}`;
}

class GiftCartGateway {
  constructor({
    routes,
    fetchImpl = typeof fetch === 'function' ? fetch.bind(globalThis) : null,
    wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  } = {}) {
    if (typeof fetchImpl !== 'function') {
      throw new TypeError('fetch inválido');
    }

    this.routes = isGiftChoiceRecord(routes) ? { ...routes } : {};
    this.fetchImpl = fetchImpl;
    this.wait = typeof wait === 'function'
      ? wait
      : (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  }

  async requestJson(url, init, errorCode) {
    if (typeof url !== 'string' || url.length === 0) {
      throw createGiftCartError(errorCode);
    }

    let response;

    try {
      response = await this.fetchImpl(url, init);
    } catch (error) {
      throw createGiftCartError(errorCode);
    }

    if (!response || response.ok !== true || typeof response.json !== 'function') {
      throw createGiftCartError(errorCode);
    }

    try {
      return await response.json();
    } catch (error) {
      throw createGiftCartError(errorCode);
    }
  }

  async getCart() {
    const configuredCart = typeof this.routes.cart === 'string'
      ? this.routes.cart
      : joinGiftRouteRoot(this.routes.root, 'cart');
    const payload = await this.requestJson(
      ensureGiftJsonSuffix(configuredCart),
      { method: 'GET' },
      'cart_fetch_failed',
    );

    if (!isConfirmedGiftCart(payload)) {
      throw createGiftCartError('cart_fetch_failed');
    }

    return cloneGiftChoiceValue(payload);
  }

  async getProduct(handle) {
    const normalizedHandle = normalizeGiftHandle(handle);

    if (!normalizedHandle) {
      throw createGiftCartError('product_fetch_failed');
    }

    const payload = await this.requestJson(
      joinGiftRouteRoot(
        this.routes.root,
        `products/${encodeURIComponent(normalizedHandle)}.js`,
      ),
      { method: 'GET' },
      'product_fetch_failed',
    );

    if (
      !isGiftChoiceRecord(payload) ||
      typeof payload.handle !== 'string' ||
      !Array.isArray(payload.variants)
    ) {
      throw createGiftCartError('product_fetch_failed');
    }

    return cloneGiftChoiceValue(payload);
  }

  async addLine({
    variantId,
    quantity,
    properties,
  } = {}) {
    if (
      variantId == null ||
      quantity !== 1 ||
      !isGiftChoiceRecord(properties)
    ) {
      throw createGiftCartError('gift_add_failed');
    }

    const payload = await this.requestJson(
      this.routes.cart_add_url,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: 1,
            properties: cloneGiftChoiceValue(properties),
          }],
        }),
      },
      'gift_add_failed',
    );

    return {
      payload: cloneGiftChoiceValue(payload),
      cartCandidate: hasGiftCartItems(payload)
        ? cloneGiftChoiceValue(payload)
        : null,
    };
  }

  async changeLine({
    lineKey,
    quantity,
  } = {}) {
    if (
      typeof lineKey !== 'string' ||
      lineKey.length === 0 ||
      !Number.isInteger(quantity) ||
      quantity < 0
    ) {
      throw createGiftCartError('gift_change_failed');
    }

    const payload = await this.requestJson(
      this.routes.cart_change_url,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: lineKey,
          quantity,
        }),
      },
      'gift_change_failed',
    );

    return {
      payload: cloneGiftChoiceValue(payload),
      cartCandidate: hasGiftCartItems(payload)
        ? cloneGiftChoiceValue(payload)
        : null,
    };
  }
}

class GiftProductResolver {
  constructor({
    gateway,
    core,
    cache = new Map(),
  } = {}) {
    if (!gateway || typeof gateway.getProduct !== 'function') {
      throw new TypeError('GiftCartGateway inválido');
    }

    if (!core || typeof core.normalizeGiftOptions !== 'function') {
      throw new TypeError('GiftChoiceCore inválido');
    }

    this.gateway = gateway;
    this.core = core;
    this.cache = cache instanceof Map ? cache : new Map();
  }

  resolveHandle(handle, { fresh = false } = {}) {
    const normalizedHandle = normalizeGiftHandle(handle);

    if (!normalizedHandle) {
      return Promise.reject(createGiftCartError('product_fetch_failed'));
    }

    if (!fresh && this.cache.has(normalizedHandle)) {
      return this.cache.get(normalizedHandle);
    }

    const request = Promise.resolve()
      .then(() => this.gateway.getProduct(normalizedHandle))
      .then((product) => cloneGiftChoiceValue(product));

    this.cache.set(normalizedHandle, request);
    request.catch(() => {
      if (this.cache.get(normalizedHandle) === request) {
        this.cache.delete(normalizedHandle);
      }
    });

    return request;
  }

  async resolveConfigured(handles, { fresh = false } = {}) {
    const normalizedHandles = Array.isArray(handles)
      ? handles
        .map(normalizeGiftHandle)
        .filter(Boolean)
      : [];
    const settled = await Promise.allSettled(
      normalizedHandles.map((handle) => (
        this.resolveHandle(handle, { fresh: fresh === true })
      )),
    );
    const products = settled
      .filter((result) => result.status === 'fulfilled')
      .map((result) => result.value);

    return this.core.normalizeGiftOptions(products);
  }

  async resolveChoiceOptions(handles, { fresh = false } = {}) {
    const normalizedHandles = Array.isArray(handles)
      ? Array.from(new Set(
        handles
          .map(normalizeGiftHandle)
          .filter(Boolean),
      ))
      : [];
    const settled = await Promise.allSettled(
      normalizedHandles.map((handle) => (
        this.resolveHandle(handle, { fresh: fresh === true })
      )),
    );
    const options = [];
    const seenVariantIds = new Set();

    settled.forEach((result) => {
      if (
        result.status !== 'fulfilled' ||
        !isGiftChoiceRecord(result.value)
      ) {
        return;
      }

      const product = result.value;
      const handle = normalizeGiftHandle(product.handle);
      const variant = Array.isArray(product.variants)
        ? product.variants[0]
        : null;

      if (!handle || !variant || variant.id == null) {
        return;
      }

      const variantKey = String(variant.id);

      if (seenVariantIds.has(variantKey)) {
        return;
      }

      seenVariantIds.add(variantKey);
      options.push({
        handle,
        productId: product.id ?? null,
        variantId: variant.id,
        title: typeof product.title === 'string' ? product.title : '',
        image: product.featured_image || product.image?.src || null,
        price: variant.price ?? 0,
        available: variant.available === true,
      });
    });

    if (
      normalizedHandles.length > 0 &&
      options.length === 0 &&
      settled.every((result) => result.status === 'rejected')
    ) {
      throw createGiftCartError('product_fetch_failed');
    }

    return options;
  }

  async revalidateSelection(option) {
    if (
      !isGiftChoiceRecord(option) ||
      !normalizeGiftHandle(option.handle) ||
      option.variantId == null
    ) {
      return {
        available: false,
        reason: 'invalid_selection',
        option: null,
      };
    }

    let product;

    try {
      product = await this.resolveHandle(option.handle, { fresh: true });
    } catch (error) {
      return {
        available: false,
        reason: 'product_fetch_failed',
        option: null,
      };
    }

    const variant = Array.isArray(product.variants)
      ? product.variants[0]
      : null;

    if (!variant || variant.id == null) {
      return {
        available: false,
        reason: 'variant_missing',
        option: null,
      };
    }

    if (String(variant.id) !== String(option.variantId)) {
      return {
        available: false,
        reason: 'variant_changed',
        option: null,
      };
    }

    if (variant.available !== true) {
      return {
        available: false,
        reason: 'variant_unavailable',
        option: null,
      };
    }

    const normalized = this.core.normalizeGiftOptions([product])[0] || null;

    return normalized
      ? {
        available: true,
        reason: null,
        option: normalized,
      }
      : {
        available: false,
        reason: 'variant_unavailable',
        option: null,
      };
  }
}

function getGiftChoiceLinesForLevel(cart, levelNumber) {
  const normalizedLevel = normalizeGiftChoiceLevelNumber(levelNumber);

  if (
    normalizedLevel === null ||
    !isConfirmedGiftCart(cart)
  ) {
    return [];
  }

  return cart.items.filter(
    (item) => getGiftChoiceLineLevel(item) === normalizedLevel,
  );
}

function isExactGiftChoiceLine(item, variantId) {
  return (
    isGiftChoiceLine(item) &&
    String(item.variant_id) === String(variantId) &&
    item.quantity === 1
  );
}

function clonePrivateGiftProperties(properties) {
  if (!isGiftChoiceRecord(properties)) {
    return {};
  }

  return Object.keys(properties).reduce((result, key) => {
    if (key.startsWith('_')) {
      result[key] = String(properties[key]);
    }

    return result;
  }, {});
}

function getGiftChoiceCartTotal(cart) {
  if (!isConfirmedGiftCart(cart)) {
    return 0;
  }

  return cart.items.reduce((total, item) => {
    if (isGiftChoiceLine(item)) {
      return total;
    }

    const linePrice = Number(
      item.original_line_price || item.line_price,
    );

    return total + (Number.isFinite(linePrice) ? linePrice : 0);
  }, 0) / 100;
}

function doesGiftChoiceTriggerMatch(cart, triggerProducts) {
  if (!Array.isArray(triggerProducts) || triggerProducts.length === 0) {
    return true;
  }

  if (!isConfirmedGiftCart(cart)) {
    return false;
  }

  const normalizedTriggers = triggerProducts
    .map((trigger) => {
      const value = isGiftChoiceRecord(trigger)
        ? trigger.handle || trigger.id
        : trigger;

      if (value == null) {
        return '';
      }

      const stringValue = String(value);

      return stringValue.includes('gid://shopify/Product/')
        ? stringValue.split('/').pop()
        : stringValue.trim().toLowerCase();
    })
    .filter(Boolean);

  return cart.items.some((item) => {
    if (isGiftChoiceLine(item)) {
      return false;
    }

    const productId = item.product_id == null
      ? ''
      : String(item.product_id);
    const handle = normalizeGiftHandle(item.handle);

    return normalizedTriggers.some(
      (trigger) => trigger === productId || trigger === handle,
    );
  });
}

class GiftChoiceCoordinator {
  constructor({
    config,
    core,
    decisionStore,
    cartGateway = null,
    productResolver = null,
    cartMutator = null,
    emit = () => {},
    logger = null,
    gtag = null,
    fbq = null,
    debug = false,
  }) {
    if (
      !core ||
      typeof core.resolveLevelState !== 'function' ||
      typeof core.sortPendingLevels !== 'function'
    ) {
      throw new TypeError('GiftChoiceCore inválido');
    }

    if (
      !decisionStore ||
      typeof decisionStore.getNone !== 'function' ||
      typeof decisionStore.setNone !== 'function'
    ) {
      throw new TypeError('GiftDecisionStorageAdapter inválido');
    }

    this.config = config || {};
    this.core = core;
    this.decisionStore = decisionStore;
    this.cartGateway = cartGateway;
    this.productResolver = productResolver;
    this.cartMutator = cartMutator;
    this.emit = typeof emit === 'function' ? emit : () => {};
    this.logger = logger;
    this.gtag = typeof gtag === 'function' ? gtag : null;
    this.fbq = typeof fbq === 'function' ? fbq : null;
    this.debug = debug === true;
    this.presenters = new Set();
    this.pendingByLevel = new Map();
    this.lastConfirmedCart = null;
    this.eligibilityByLevel = new Map();
    this.coordinatedRemovalKeys = new Set();
    this.reconcilePromise = null;
    this.reconcileAgain = false;
    this.latestReconcileInput = null;
    this.latestReconcileFn = null;
    this.modalOwner = null;
    this.mutationTail = Promise.resolve();
    this.mutationByKey = new Map();
    this.transientByLevel = new Map();
    this.transientUnavailableByLevel = new Map();
    this.externalFetchPromise = null;
    this.lastResolution = null;
    this.runtime = GiftSystemRuntime;
    this.choiceModalRoot = null;
    this.choiceModalElements = null;
    this.choiceModalBound = false;
    this.choiceSurfaceActionsBound = false;
    this.handleSurfaceChoiceActionBound = null;
    this.checkoutGuardInstalled = false;
    this.handleCheckoutCaptureBound = null;
    this.checkoutIntent = null;
    this.checkoutBypassOnce = false;
    this.checkoutPendingLevels = [];
    this.acceleratedCheckoutSnapshots = new WeakMap();
    this.choiceSelectionExplicit = false;
    this.choiceFocusTrapActive = false;
    this.choiceResolvedOptionsByLevel = new Map();
    this.dismissedChoiceLevels = new Set();
    this.choicePromptPromise = null;
    this.choiceModalState = {
      levelNumber: null,
      mode: 'new',
      options: [],
      currentSelection: null,
      selection: null,
      status: 'idle',
      opener: null,
      retryAction: null,
    };
  }

  registerPresenter(presenter) {
    if (presenter) {
      this.presenters.add(presenter);
    }

    return presenter;
  }

  unregisterPresenter(presenter) {
    return this.presenters.delete(presenter);
  }

  setRuntimeDependencies({
    cartGateway,
    productResolver,
    cartMutator,
  } = {}) {
    if (cartGateway) {
      this.cartGateway = cartGateway;
    }

    if (productResolver) {
      this.productResolver = productResolver;
    }

    if (cartMutator) {
      this.cartMutator = cartMutator;
    }

    return this;
  }

  bindChoiceSurfaceActions() {
    if (this.choiceSurfaceActionsBound) {
      return true;
    }

    const documentRef = this.runtime && this.runtime.document;

    if (!documentRef || typeof documentRef.addEventListener !== 'function') {
      return false;
    }

    this.handleSurfaceChoiceActionBound = (event) => (
      this.handleSurfaceChoiceAction(event)
    );
    documentRef.addEventListener(
      'click',
      this.handleSurfaceChoiceActionBound,
    );
    this.choiceSurfaceActionsBound = true;

    return true;
  }

  refreshChoiceSurfaceActions(cartSnapshot) {
    if (!this.config || this.config.choiceEnabled !== true) {
      return [];
    }

    const documentRef = this.runtime && this.runtime.document;

    if (
      !documentRef ||
      typeof documentRef.querySelectorAll !== 'function' ||
      typeof documentRef.createElement !== 'function'
    ) {
      return [];
    }

    const snapshot = isGiftChoiceRecord(cartSnapshot)
      ? cartSnapshot
      : this.lastResolution;
    const levels = (
      isGiftChoiceRecord(snapshot) &&
      Array.isArray(snapshot.levels)
    )
      ? snapshot.levels
      : [];
    const translations = (
      this.runtime &&
      this.runtime.giftSystemTranslations &&
      this.runtime.giftSystemTranslations.choice
    ) || {};
    const actionSpecs = levels
      .filter((level) => (
        isGiftChoiceRecord(level) &&
        level.eligible === true &&
        (level.status === 'gift' || level.status === 'none')
      ))
      .map((level) => {
        const levelNumber = normalizeGiftChoiceLevelNumber(
          level.levelNumber,
        );

        if (levelNumber === null) {
          return null;
        }

        if (level.status === 'none') {
          return {
            action: 'reconsider',
            levelNumber,
            label: (
              translations.reconsider_none ||
              'Reconsiderar brinde'
            ),
            variantId: null,
          };
        }

        const giftLine = Array.isArray(level.giftLines)
          ? level.giftLines.find((line) => (
            isGiftChoiceRecord(line) &&
            line.variant_id != null
          ))
          : null;

        if (!giftLine) {
          return null;
        }

        return {
          action: 'change',
          levelNumber,
          label: translations.change_gift || 'Trocar brinde',
          variantId: String(giftLine.variant_id),
        };
      })
      .filter(Boolean);
    const renderedButtons = [];

    documentRef
      .querySelectorAll('[data-gift-choice-surface-actions]')
      .forEach((slot) => {
        const buttons = actionSpecs.map((spec) => {
          const button = documentRef.createElement('button');

          button.type = 'button';
          button.className = 'button button--secondary';
          button.dataset.giftChoiceAction = spec.action;
          button.dataset.giftLevel = String(spec.levelNumber);
          button.textContent = spec.label;

          if (spec.variantId !== null) {
            button.dataset.giftVariantId = spec.variantId;
          }

          renderedButtons.push(button);
          return button;
        });

        if (slot && typeof slot.replaceChildren === 'function') {
          slot.replaceChildren(...buttons);
        }
      });

    return renderedButtons;
  }

  handleSurfaceChoiceAction(event) {
    const target = event && event.target;

    if (!target || typeof target.closest !== 'function') {
      return null;
    }

    const button = target.closest(
      '[data-gift-choice-action][data-gift-level]',
    );

    if (!button || button.isConnected !== true || !button.dataset) {
      return null;
    }

    const levelNumber = Number(button.dataset.giftLevel);

    if (!Number.isInteger(levelNumber) || levelNumber < 1) {
      return null;
    }

    if (
      button.dataset.giftChoiceAction === 'change' &&
      typeof button.dataset.giftVariantId === 'string' &&
      button.dataset.giftVariantId.length > 0
    ) {
      return this.openChoiceModal({
        levelNumber,
        mode: 'swap',
        currentSelection: {
          kind: 'gift',
          variantId: button.dataset.giftVariantId,
        },
        opener: button,
      });
    }

    if (button.dataset.giftChoiceAction === 'reconsider') {
      return this.openChoiceModal({
        levelNumber,
        mode: 'reconsider',
        currentSelection: { kind: 'none' },
        opener: button,
      });
    }

    return null;
  }

  /**
   * Guarda client-side de UX; nao e enforcement servidor e nao impede navegacao direta ou checkout iniciado fora destes controles.
   */
  installCheckoutGuard() {
    if (this.checkoutGuardInstalled) {
      return true;
    }

    const documentRef = this.runtime && this.runtime.document;

    if (!documentRef || typeof documentRef.addEventListener !== 'function') {
      return false;
    }

    this.handleCheckoutCaptureBound = (event) => (
      this.handleCheckoutCapture(event)
    );
    documentRef.addEventListener(
      'click',
      this.handleCheckoutCaptureBound,
      true,
    );
    this.checkoutGuardInstalled = true;

    return true;
  }

  getPendingLevels() {
    const levels = Array.from(this.pendingByLevel.values()).filter(
      (level) => (
        isGiftChoiceRecord(level) &&
        level.status === 'pending' &&
        !this.transientUnavailableByLevel.has(level.levelNumber)
      ),
    );

    return this.core.sortPendingLevels(
      cloneGiftChoiceValue(levels),
    );
  }

  captureCheckoutIntent(button) {
    if (
      !button ||
      !['cart-checkout', 'cart-sidebar-checkout'].includes(button.id)
    ) {
      return null;
    }

    const data = button.dataset || {};
    const surface = ['cart', 'quick-cart'].includes(
      data.giftCheckoutSurface,
    )
      ? data.giftCheckoutSurface
      : (
        button.id === 'cart-sidebar-checkout'
          ? 'quick-cart'
          : 'cart'
      );
    let destination = (
      typeof data.giftCheckoutUrl === 'string' &&
      data.giftCheckoutUrl.length > 0
    )
      ? data.giftCheckoutUrl
      : '';

    if (
      !destination &&
      typeof button.getAttribute === 'function'
    ) {
      destination = (
        button.getAttribute('data-href') ||
        button.getAttribute('formaction') ||
        ''
      );
    }

    if (
      !destination &&
      button.form &&
      typeof button.form.action === 'string'
    ) {
      destination = button.form.action;
    }

    if (!destination) {
      destination = '/checkout';
    }

    this.checkoutIntent = {
      surface,
      buttonId: button.id,
      destination,
      opener: button.isConnected === true ? button : null,
    };

    return this.checkoutIntent;
  }

  handleCheckoutCapture(event) {
    const target = event && event.target;

    if (!target || typeof target.closest !== 'function') {
      return null;
    }

    const button = target.closest(
      '#cart-checkout, #cart-sidebar-checkout',
    );

    if (
      !button ||
      !['cart-checkout', 'cart-sidebar-checkout'].includes(button.id)
    ) {
      return null;
    }

    if (this.checkoutBypassOnce === true) {
      this.checkoutBypassOnce = false;
      return null;
    }

    if (
      !this.config ||
      this.config.choiceEnabled !== true ||
      button.disabled === true
    ) {
      return null;
    }

    const pendingLevels = this.getPendingLevels();

    if (pendingLevels.length === 0) {
      return null;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    this.captureCheckoutIntent(button);
    this.checkoutPendingLevels = pendingLevels.map(
      (level) => level.levelNumber,
    );

    return this.openNextPendingForCheckout();
  }

  async openNextPendingForCheckout() {
    if (!this.checkoutIntent) {
      return null;
    }

    const pendingLevels = this.getPendingLevels();

    this.checkoutPendingLevels = pendingLevels.map(
      (level) => level.levelNumber,
    );

    if (pendingLevels.length === 0) {
      if (this.modalOwner !== null) {
        return null;
      }

      return this.resumeCheckoutIntent();
    }

    const nextPending = pendingLevels[0];
    const levelNumber = nextPending.levelNumber;

    this.dismissedChoiceLevels.delete(levelNumber);

    if (this.modalOwner !== null) {
      if (this.modalOwner === levelNumber) {
        this.choiceModalState.opener = this.checkoutIntent.opener;
      }

      return null;
    }

    return this.openChoiceModal({
      levelNumber,
      mode: 'new',
      opener: this.checkoutIntent.opener,
    });
  }

  resumeCheckoutIntent() {
    if (!this.checkoutIntent) {
      return null;
    }

    const intent = this.checkoutIntent;
    const documentRef = this.runtime && this.runtime.document;
    let button = (
      intent.opener &&
      intent.opener.isConnected === true
    )
      ? intent.opener
      : null;

    this.checkoutIntent = null;
    this.checkoutPendingLevels = [];

    if (
      !button &&
      documentRef &&
      typeof documentRef.querySelector === 'function'
    ) {
      button = documentRef.querySelector(`#${intent.buttonId}`);
    }

    if (
      button &&
      button.isConnected === true &&
      typeof button.click === 'function'
    ) {
      this.checkoutBypassOnce = true;

      try {
        button.click();
      } finally {
        this.checkoutBypassOnce = false;
      }

      return {
        resumed: 'button',
        buttonId: intent.buttonId,
      };
    }

    this.checkoutBypassOnce = false;

    if (
      typeof intent.destination === 'string' &&
      intent.destination.length > 0 &&
      this.runtime &&
      this.runtime.location &&
      typeof this.runtime.location.assign === 'function'
    ) {
      this.runtime.location.assign(intent.destination);

      return {
        resumed: 'destination',
        destination: intent.destination,
      };
    }

    return null;
  }

  cancelCheckoutIntent() {
    const hadIntent = this.checkoutIntent !== null;

    this.checkoutIntent = null;
    this.checkoutBypassOnce = false;
    this.checkoutPendingLevels = [];

    return hadIntent;
  }

  snapshotAcceleratedCheckout(container) {
    if (
      !container ||
      typeof container.hasAttribute !== 'function' ||
      typeof container.getAttribute !== 'function'
    ) {
      return null;
    }

    if (this.acceleratedCheckoutSnapshots.has(container)) {
      return this.acceleratedCheckoutSnapshots.get(container);
    }

    const snapshot = {
      hiddenPresent: container.hasAttribute('hidden'),
      hiddenValue: container.getAttribute('hidden'),
      hiddenProperty: container.hidden,
      inertPresent: container.hasAttribute('inert'),
      inertValue: container.getAttribute('inert'),
      inertProperty: container.inert,
      ariaHiddenPresent: container.hasAttribute('aria-hidden'),
      ariaHiddenValue: container.getAttribute('aria-hidden'),
    };

    this.acceleratedCheckoutSnapshots.set(container, snapshot);
    return snapshot;
  }

  suspendAcceleratedCheckout(container) {
    const snapshot = this.snapshotAcceleratedCheckout(container);

    if (
      !snapshot ||
      typeof container.setAttribute !== 'function'
    ) {
      return false;
    }

    container.hidden = true;
    container.inert = true;
    container.setAttribute('hidden', '');
    container.setAttribute('inert', '');
    container.setAttribute('aria-hidden', 'true');

    return true;
  }

  restoreAcceleratedCheckout(container) {
    if (
      !container ||
      !this.acceleratedCheckoutSnapshots.has(container) ||
      typeof container.setAttribute !== 'function' ||
      typeof container.removeAttribute !== 'function'
    ) {
      return false;
    }

    const snapshot = this.acceleratedCheckoutSnapshots.get(container);

    this.acceleratedCheckoutSnapshots.delete(container);

    if (snapshot.hiddenPresent) {
      container.setAttribute('hidden', snapshot.hiddenValue);
    } else {
      container.removeAttribute('hidden');
    }

    if (snapshot.inertPresent) {
      container.setAttribute('inert', snapshot.inertValue);
    } else {
      container.removeAttribute('inert');
    }

    if (snapshot.ariaHiddenPresent) {
      container.setAttribute('aria-hidden', snapshot.ariaHiddenValue);
    } else {
      container.removeAttribute('aria-hidden');
    }

    if (!Object.is(container.hidden, snapshot.hiddenProperty)) {
      container.hidden = snapshot.hiddenProperty;
    }

    if (!Object.is(container.inert, snapshot.inertProperty)) {
      container.inert = snapshot.inertProperty;
    }

    return true;
  }

  syncAcceleratedCheckoutAvailability() {
    const documentRef = this.runtime && this.runtime.document;

    if (
      !documentRef ||
      typeof documentRef.querySelectorAll !== 'function'
    ) {
      return [];
    }

    const containers = Array.from(
      documentRef.querySelectorAll('[data-gift-accelerated-checkout]'),
    ).filter((container) => (
      container && container.isConnected !== false
    ));
    const shouldSuspend = (
      this.config &&
      this.config.choiceEnabled === true &&
      this.getPendingLevels().length > 0
    );

    containers.forEach((container) => {
      if (shouldSuspend) {
        this.suspendAcceleratedCheckout(container);
      } else {
        this.restoreAcceleratedCheckout(container);
      }
    });

    return containers;
  }

  bindChoiceModal() {
    if (this.choiceModalBound) {
      return true;
    }

    const documentRef = this.runtime && this.runtime.document;

    if (!documentRef || typeof documentRef.querySelector !== 'function') {
      return false;
    }

    const modalRoot = documentRef.querySelector('[data-gift-choice-modal]');

    if (!modalRoot || typeof modalRoot.querySelector !== 'function') {
      return false;
    }

    this.choiceModalRoot = modalRoot;
    this.choiceModalElements = {
      panel: modalRoot.querySelector('[role="dialog"]'),
      title: modalRoot.querySelector('#GiftChoiceTitle'),
      options: modalRoot.querySelector('[data-gift-choice-options]'),
      optionTemplate: modalRoot.querySelector(
        '[data-gift-choice-option-template]',
      ),
      none: modalRoot.querySelector('[data-gift-choice-none]'),
      status: modalRoot.querySelector('[data-gift-choice-status]'),
      error: modalRoot.querySelector('[data-gift-choice-error]'),
      retry: modalRoot.querySelector('[data-gift-choice-retry]'),
      confirm: modalRoot.querySelector('[data-gift-choice-confirm]'),
      close: modalRoot.querySelector('[data-gift-choice-close]'),
    };
    this.boundChoiceChange = (event) => this.handleChoiceChange(event);
    this.boundChoiceClick = (event) => this.handleChoiceClick(event);
    this.boundChoiceKeydown = (event) => this.handleChoiceKeydown(event);
    modalRoot.addEventListener('change', this.boundChoiceChange);
    modalRoot.addEventListener('click', this.boundChoiceClick);
    modalRoot.addEventListener('keydown', this.boundChoiceKeydown);
    this.choiceModalBound = true;

    return true;
  }

  setChoiceModalState(
    status,
    {
      message = '',
      retryAction = null,
    } = {},
  ) {
    const normalizedStatus = [
      'idle',
      'loading',
      'submitting',
      'error',
      'unavailable',
    ].includes(status)
      ? status
      : 'idle';
    const translations = (
      this.runtime &&
      this.runtime.giftSystemTranslations &&
      this.runtime.giftSystemTranslations.choice
    ) || {};
    const isBusy = (
      normalizedStatus === 'loading' ||
      normalizedStatus === 'submitting'
    );
    let stateMessage = typeof message === 'string' ? message : '';

    if (!stateMessage && normalizedStatus === 'loading') {
      stateMessage = translations.loading_options || 'Carregando opções…';
    } else if (!stateMessage && normalizedStatus === 'submitting') {
      stateMessage = (
        this.choiceModalState.selection &&
        this.choiceModalState.selection.kind === 'none'
      )
        ? (
          translations.saving_none ||
          'Salvando sua escolha…'
        )
        : (
          translations.adding ||
          'Adicionando brinde…'
        );
    } else if (!stateMessage && normalizedStatus === 'error') {
      stateMessage = (
        translations.generic_error ||
        'Não foi possível concluir. Tente novamente.'
      );
    } else if (!stateMessage && normalizedStatus === 'unavailable') {
      stateMessage = translations.unavailable || 'Indisponível';
    }

    this.choiceModalState.status = normalizedStatus;
    this.choiceModalState.retryAction = (
      (normalizedStatus === 'error' || normalizedStatus === 'unavailable') &&
      typeof retryAction === 'function'
    )
      ? retryAction
      : null;

    if (!this.choiceModalElements) {
      return this.choiceModalState;
    }

    const {
      status: statusElement,
      error,
      retry,
      confirm,
      close,
    } = this.choiceModalElements;

    if (statusElement) {
      const showStatus = (
        normalizedStatus === 'loading' ||
        normalizedStatus === 'submitting' ||
        normalizedStatus === 'unavailable'
      ) && stateMessage.length > 0;

      statusElement.textContent = showStatus ? stateMessage : '';
      statusElement.hidden = !showStatus;
    }

    if (error) {
      const showError = (
        normalizedStatus === 'error' &&
        stateMessage.length > 0
      );

      error.textContent = showError ? stateMessage : '';
      error.hidden = !showError;
    }

    if (
      this.choiceModalRoot &&
      typeof this.choiceModalRoot.querySelectorAll === 'function'
    ) {
      this.choiceModalRoot
        .querySelectorAll('input[name="gift-choice-selection"]')
        .forEach((input) => {
          const optionUnavailable = (
            input.dataset &&
            input.dataset.giftChoiceAvailable === 'false'
          );

          input.disabled = isBusy || optionUnavailable;
        });
      this.choiceModalRoot.dataset.giftChoiceState = normalizedStatus;
    }

    if (confirm) {
      confirm.disabled = (
        isBusy ||
        !this.choiceSelectionExplicit ||
        !this.choiceModalState.selection
      );
    }

    if (close) {
      close.disabled = isBusy;
    }

    if (retry) {
      const showRetry = (
        !isBusy &&
        typeof this.choiceModalState.retryAction === 'function'
      );

      retry.hidden = !showRetry;
      retry.disabled = isBusy;
    }

    return this.choiceModalState;
  }

  async loadChoiceLevelOptions(
    levelNumber,
    { forceRefresh = false } = {},
  ) {
    this.setChoiceModalState('loading');

    try {
      const options = await this.resolveLevelOptions(
        levelNumber,
        { forceRefresh },
      );

      this.choiceModalState.options = cloneGiftChoiceValue(options);

      if (
        this.choiceModalState.selection &&
        this.choiceModalState.selection.kind === 'gift' &&
        !options.some((option) => (
          option.available === true &&
          String(option.variantId) === String(
            this.choiceModalState.selection.variantId,
          )
        ))
      ) {
        this.choiceModalState.selection = null;
        this.choiceSelectionExplicit = false;
      }

      this.renderChoiceOptions(options);

      if (options.some((option) => option.available === true)) {
        if (this.transientUnavailableByLevel.has(levelNumber)) {
          this.clearTransientUnavailableFromRetry(levelNumber);
          await this.retryLevel(levelNumber);
        }

        this.setChoiceModalState('idle');
      } else {
        const retryAction = (
          { forceRefresh: refresh = true } = {}
        ) => this.loadChoiceLevelOptions(
          levelNumber,
          { forceRefresh: refresh },
        );

        this.failOpenLevel({
          levelNumber,
          code: 'gift_options_unavailable',
          cart: this.lastConfirmedCart,
        });

        if (this.checkoutIntent) {
          this.closeChoiceModal({ reason: 'unavailable' });
        } else {
          if (this.modalOwner === null) {
            this.claimModal(levelNumber);
          }

          this.setChoiceModalState('unavailable', { retryAction });
        }
      }

      this.activateChoiceFocusTrap();
      return cloneGiftChoiceValue(options);
    } catch (error) {
      this.setChoiceModalState('error', {
        retryAction: ({ forceRefresh: refresh = true } = {}) => (
          this.loadChoiceLevelOptions(
            levelNumber,
            { forceRefresh: refresh },
          )
        ),
      });

      return null;
    }
  }

  async openChoiceModal({
    levelNumber,
    mode = 'new',
    currentSelection = null,
    opener = (
      this.runtime &&
      this.runtime.document &&
      this.runtime.document.activeElement
    ),
  } = {}) {
    const normalizedLevel = normalizeGiftChoiceLevelNumber(levelNumber);
    const normalizedMode = ['new', 'swap', 'reconsider'].includes(mode)
      ? mode
      : 'new';

    if (normalizedLevel === null) {
      return null;
    }

    try {
      if (!this.bindChoiceModal()) {
        return this.failOpenLevel({
          levelNumber: normalizedLevel,
          code: 'gift_choice_modal_missing',
          cart: this.lastConfirmedCart,
        });
      }

      if (
        this.modalOwner !== normalizedLevel &&
        !this.claimModal(normalizedLevel)
      ) {
        return null;
      }

      const HTMLElementConstructor = this.runtime && this.runtime.HTMLElement;
      const normalizedCurrentSelection = (
        isGiftChoiceRecord(currentSelection) &&
        currentSelection.kind === 'gift' &&
        currentSelection.variantId != null
      )
        ? {
          kind: 'gift',
          variantId: String(currentSelection.variantId),
        }
        : (
          isGiftChoiceRecord(currentSelection) &&
          currentSelection.kind === 'none'
            ? { kind: 'none' }
            : null
        );
      const validOpener = (
        typeof HTMLElementConstructor === 'function' &&
        opener instanceof HTMLElementConstructor
      )
        ? opener
        : null;

      this.choiceModalState = {
        levelNumber: normalizedLevel,
        mode: normalizedMode,
        options: [],
        currentSelection: normalizedCurrentSelection,
        selection: normalizedMode === 'new'
          ? null
          : cloneGiftChoiceValue(normalizedCurrentSelection),
        status: 'idle',
        opener: validOpener,
        retryAction: null,
      };
      this.choiceSelectionExplicit = false;
      this.choiceModalRoot.hidden = false;
      this.setChoiceModalState('loading');
      this.activateChoiceFocusTrap();

      return this.loadChoiceLevelOptions(normalizedLevel);
    } catch (error) {
      return this.failOpenLevel({
        levelNumber: normalizedLevel,
        code: 'gift_choice_modal_failed',
        cart: this.lastConfirmedCart,
        error,
      });
    }
  }

  renderChoiceOptions(options) {
    if (!this.choiceModalElements) {
      return [];
    }

    const normalizedOptions = Array.isArray(options)
      ? options.filter(isGiftChoiceRecord)
      : [];
    const container = this.choiceModalElements.options;
    const template = this.choiceModalElements.optionTemplate;

    if (
      container &&
      typeof container.replaceChildren === 'function'
    ) {
      container.replaceChildren();
    }

    if (
      container &&
      template &&
      template.content &&
      typeof template.content.cloneNode === 'function'
    ) {
      normalizedOptions.forEach((option) => {
        const fragment = template.content.cloneNode(true);
        const input = fragment.querySelector(
          'input[name="gift-choice-selection"]',
        );
        const label = fragment.querySelector('label');
        const image = fragment.querySelector(
          '.gift-choice__option-image',
        );
        const name = fragment.querySelector(
          '[data-gift-choice-option-name]',
        );
        const unavailable = fragment.querySelector(
          '[data-gift-choice-option-unavailable]',
        );
        const variantId = String(option.variantId);
        const safeVariantId = variantId.replace(
          /[^A-Za-z0-9_-]/gu,
          '-',
        );
        const inputId = (
          `GiftChoice-${this.choiceModalState.levelNumber}-${safeVariantId}`
        );
        const isSelected = (
          this.choiceModalState.selection &&
          this.choiceModalState.selection.kind === 'gift' &&
          String(this.choiceModalState.selection.variantId) === variantId
        );

        if (input) {
          input.id = inputId;
          input.value = `gift:${variantId}`;
          input.disabled = option.available !== true;
          input.checked = isSelected;

          if (input.dataset) {
            input.dataset.giftChoiceAvailable = (
              option.available === true ? 'true' : 'false'
            );
          }
        }

        if (label) {
          label.htmlFor = inputId;
        }

        if (image) {
          if (
            typeof option.imageUrl === 'string' &&
            option.imageUrl.length > 0
          ) {
            image.src = option.imageUrl;
          } else if (typeof image.removeAttribute === 'function') {
            image.removeAttribute('src');
          }
        }

        if (name) {
          name.textContent = typeof option.title === 'string'
            ? option.title
            : '';
        }

        if (unavailable) {
          unavailable.hidden = option.available === true;
        }

        container.appendChild(fragment);
      });
    }

    if (this.choiceModalElements.none) {
      this.choiceModalElements.none.checked = (
        this.choiceModalState.selection &&
        this.choiceModalState.selection.kind === 'none'
      );
    }

    if (this.choiceModalElements.confirm) {
      this.choiceModalElements.confirm.disabled = true;
    }

    return cloneGiftChoiceValue(normalizedOptions);
  }

  markChoiceOptionUnavailable(levelNumber, variantId) {
    const normalizedLevel = normalizeGiftChoiceLevelNumber(levelNumber);
    const normalizedVariantId = variantId == null
      ? ''
      : String(variantId);

    if (normalizedLevel === null || normalizedVariantId.length === 0) {
      return [];
    }

    const markUnavailable = (options) => (
      Array.isArray(options)
        ? options.map((option) => (
          isGiftChoiceRecord(option) &&
          String(option.variantId) === normalizedVariantId
            ? { ...option, available: false }
            : cloneGiftChoiceValue(option)
        ))
        : []
    );
    const resolvedOptions = this.choiceResolvedOptionsByLevel.get(
      normalizedLevel,
    );

    if (Array.isArray(resolvedOptions)) {
      this.choiceResolvedOptionsByLevel.set(
        normalizedLevel,
        markUnavailable(resolvedOptions),
      );
    }

    if (
      normalizeGiftChoiceLevelNumber(
        this.choiceModalState.levelNumber,
      ) !== normalizedLevel
    ) {
      return [];
    }

    this.choiceModalState.options = markUnavailable(
      this.choiceModalState.options,
    );

    if (
      this.choiceModalState.selection &&
      this.choiceModalState.selection.kind === 'gift' &&
      String(this.choiceModalState.selection.variantId) === normalizedVariantId
    ) {
      this.choiceModalState.selection = null;
      this.choiceSelectionExplicit = false;
    }

    this.renderChoiceOptions(this.choiceModalState.options);

    return cloneGiftChoiceValue(this.choiceModalState.options);
  }

  handleChoiceChange(event) {
    const input = event && event.target;

    if (
      !input ||
      typeof input.matches !== 'function' ||
      !input.matches('input[name="gift-choice-selection"]') ||
      input.checked !== true ||
      input.disabled === true
    ) {
      return null;
    }

    let selection = null;

    if (input.value === 'none') {
      selection = { kind: 'none' };
    } else if (
      typeof input.value === 'string' &&
      input.value.startsWith('gift:')
    ) {
      const variantId = input.value.slice('gift:'.length);
      const selectedOption = this.choiceModalState.options.find(
        (option) => (
          option.available === true &&
          String(option.variantId) === variantId
        ),
      );

      if (selectedOption) {
        selection = { kind: 'gift', variantId };
      }
    }

    if (!selection) {
      return null;
    }

    this.choiceModalState.selection = selection;
    this.choiceSelectionExplicit = true;
    this.setChoiceModalState('idle');

    return cloneGiftChoiceValue(selection);
  }

  handleChoiceClick(event) {
    const target = event && event.target;

    if (!target) {
      return null;
    }

    if (target === this.choiceModalRoot) {
      return this.closeChoiceModal({ reason: 'dismiss' });
    }

    if (typeof target.closest !== 'function') {
      return null;
    }

    if (target.closest('[data-gift-choice-close]')) {
      return this.closeChoiceModal({ reason: 'dismiss' });
    }

    if (target.closest('[data-gift-choice-confirm]')) {
      return this.confirmChoice();
    }

    if (
      target.closest('[data-gift-choice-retry]') &&
      typeof this.retryChoice === 'function'
    ) {
      return this.retryChoice();
    }

    return null;
  }

  getChoiceFocusableElements() {
    if (
      !this.choiceModalRoot ||
      typeof this.choiceModalRoot.querySelectorAll !== 'function'
    ) {
      return [];
    }

    const candidates = Array.from(
      this.choiceModalRoot.querySelectorAll([
        'a[href]',
        'button',
        'input',
        'select',
        'textarea',
        '[tabindex]:not([tabindex="-1"])',
      ].join(',')),
    );

    return candidates.filter((element) => {
      if (
        !element ||
        element.isConnected !== true ||
        element.disabled === true ||
        element.hidden === true
      ) {
        return false;
      }

      if (
        typeof element.getAttribute === 'function' &&
        (
          element.getAttribute('aria-hidden') === 'true' ||
          element.getAttribute('tabindex') === '-1'
        )
      ) {
        return false;
      }

      if (
        typeof element.closest === 'function' &&
        element.closest('[hidden]')
      ) {
        return false;
      }

      if (
        this.runtime &&
        typeof this.runtime.getComputedStyle === 'function'
      ) {
        const style = this.runtime.getComputedStyle(element);

        if (
          style &&
          (
            style.display === 'none' ||
            style.visibility === 'hidden'
          )
        ) {
          return false;
        }
      }

      return typeof element.focus === 'function';
    });
  }

  activateChoiceFocusTrap() {
    if (!this.choiceModalRoot || this.choiceModalRoot.hidden === true) {
      return null;
    }

    this.choiceFocusTrapActive = true;

    const firstAvailableRadio = (
      typeof this.choiceModalRoot.querySelector === 'function'
    )
      ? this.choiceModalRoot.querySelector(
        'input[name="gift-choice-selection"]:not([disabled])',
      )
      : null;
    const title = this.choiceModalElements
      ? this.choiceModalElements.title
      : null;
    const target = (
      firstAvailableRadio &&
      firstAvailableRadio.isConnected === true
    )
      ? firstAvailableRadio
      : title;

    if (
      target &&
      target.isConnected === true &&
      typeof target.focus === 'function'
    ) {
      target.focus();
      return target;
    }

    return null;
  }

  deactivateChoiceFocusTrap({ restoreFocus = true } = {}) {
    const focusTarget = restoreFocus === true
      ? this.resolveChoiceFocusFallback()
      : null;

    this.choiceFocusTrapActive = false;

    if (
      focusTarget &&
      focusTarget.isConnected === true &&
      typeof focusTarget.focus === 'function'
    ) {
      focusTarget.focus();
    }

    return focusTarget;
  }

  resolveChoiceFocusFallback() {
    const opener = this.choiceModalState.opener;

    if (
      opener &&
      opener.isConnected === true &&
      typeof opener.focus === 'function'
    ) {
      return opener;
    }

    const documentRef = this.runtime && this.runtime.document;

    if (!documentRef || typeof documentRef.querySelector !== 'function') {
      return null;
    }

    const levelNumber = normalizeGiftChoiceLevelNumber(
      this.choiceModalState.levelNumber,
    );
    const selectors = [
      (
        levelNumber === null
          ? null
          : (
            '[data-gift-choice-action]' +
            `[data-gift-level="${levelNumber}"]`
          )
      ),
      '#cart-sidebar-checkout',
      '#cart-checkout',
      '#MainContent',
    ].filter(Boolean);

    for (const selector of selectors) {
      const candidate = documentRef.querySelector(selector);

      if (
        candidate &&
        candidate.isConnected === true &&
        typeof candidate.focus === 'function'
      ) {
        return candidate;
      }
    }

    return null;
  }

  handleChoiceKeydown(event) {
    if (!event) {
      return null;
    }

    if (
      event.key === 'Escape' &&
      ['idle', 'error', 'unavailable'].includes(
        this.choiceModalState.status,
      )
    ) {
      event.preventDefault();
      return this.closeChoiceModal({ reason: 'dismiss' });
    }

    if (
      event.key !== 'Tab' ||
      this.choiceFocusTrapActive !== true
    ) {
      return null;
    }

    const focusableElements = this.getChoiceFocusableElements();
    const documentRef = this.runtime && this.runtime.document;
    const activeElement = documentRef
      ? documentRef.activeElement
      : null;

    if (focusableElements.length === 0) {
      event.preventDefault();

      const title = this.choiceModalElements
        ? this.choiceModalElements.title
        : null;

      if (
        title &&
        title.isConnected === true &&
        typeof title.focus === 'function'
      ) {
        title.focus();
      }

      return title;
    }

    const first = focusableElements[0];
    const last = focusableElements[focusableElements.length - 1];
    const activeInside = (
      this.choiceModalRoot &&
      typeof this.choiceModalRoot.contains === 'function' &&
      this.choiceModalRoot.contains(activeElement)
    );

    if (
      event.shiftKey === true &&
      (activeElement === first || !activeInside)
    ) {
      event.preventDefault();
      last.focus();
      return last;
    }

    if (
      event.shiftKey !== true &&
      (activeElement === last || !activeInside)
    ) {
      event.preventDefault();
      first.focus();
      return first;
    }

    return null;
  }

  async confirmChoice() {
    const {
      levelNumber,
      selection,
      status,
    } = this.choiceModalState;

    if (
      normalizeGiftChoiceLevelNumber(levelNumber) === null ||
      !selection ||
      status === 'loading' ||
      status === 'submitting'
    ) {
      return null;
    }

    const retryAction = () => this.confirmChoice();

    this.setChoiceModalState('submitting');

    try {
      const result = selection.kind === 'gift'
        ? await this.commitGiftDecision(levelNumber, selection.variantId)
        : await this.commitNoneDecision(levelNumber);
      const recoverableError = (
        result &&
        result.status !== 'unavailable' &&
        result.error &&
        result.error.recoverable === true
      )
        ? result.error
        : null;

      if (recoverableError) {
        const optionUnavailable = (
          recoverableError.optionUnavailable === true &&
          recoverableError.variantId != null
        );

        if (optionUnavailable) {
          this.markChoiceOptionUnavailable(
            levelNumber,
            recoverableError.variantId,
          );
        }

        if (this.modalOwner === null) {
          this.claimModal(levelNumber);
        }

        const translations = (
          this.runtime &&
          this.runtime.giftSystemTranslations &&
          this.runtime.giftSystemTranslations.choice
        ) || {};
        const recoverableRetryAction = optionUnavailable
          ? ({ forceRefresh = true } = {}) => (
            this.loadChoiceLevelOptions(levelNumber, { forceRefresh })
          )
          : retryAction;

        this.setChoiceModalState('error', {
          message: optionUnavailable
            ? translations.unavailable || 'Indisponível'
            : '',
          retryAction: recoverableRetryAction,
        });
      } else if (
        result &&
        (result.status === 'gift' || result.status === 'none')
      ) {
        this.closeChoiceModal({ reason: 'resolved' });
      } else if (result && result.status === 'unavailable') {
        if (this.checkoutIntent) {
          this.closeChoiceModal({ reason: 'unavailable' });
        } else {
          if (this.modalOwner === null) {
            this.claimModal(levelNumber);
          }

          this.setChoiceModalState('unavailable', { retryAction });
        }
      } else {
        if (this.modalOwner === null) {
          this.claimModal(levelNumber);
        }

        this.setChoiceModalState('error', { retryAction });
      }

      return result;
    } catch (error) {
      if (this.modalOwner === null) {
        this.claimModal(levelNumber);
      }

      this.setChoiceModalState('error', { retryAction });
      return null;
    }
  }

  async retryChoice() {
    const {
      levelNumber,
      retryAction,
      status,
    } = this.choiceModalState;

    if (
      (status !== 'error' && status !== 'unavailable') ||
      typeof retryAction !== 'function'
    ) {
      return null;
    }

    this.choiceModalState.retryAction = null;
    this.setChoiceModalState('loading');

    try {
      await this.retryLevel(levelNumber);
      this.setChoiceModalState('idle');
      return await retryAction({ forceRefresh: true });
    } catch (error) {
      this.setChoiceModalState('error', { retryAction });
      return null;
    }
  }

  closeChoiceModal({
    reason = 'dismiss',
    restoreFocus = true,
  } = {}) {
    const levelNumber = this.choiceModalState.levelNumber;

    if (
      reason === 'dismiss' &&
      (
        this.choiceModalState.status === 'loading' ||
        this.choiceModalState.status === 'submitting'
      )
    ) {
      return null;
    }

    if (reason === 'dismiss' && levelNumber !== null) {
      this.cancelCheckoutIntent();
      this.dismissedChoiceLevels.add(levelNumber);
    } else if (reason === 'resolved' && levelNumber !== null) {
      this.dismissedChoiceLevels.delete(levelNumber);
    }

    if (this.choiceModalRoot) {
      this.choiceModalRoot.hidden = true;
    }

    this.deactivateChoiceFocusTrap({
      restoreFocus: restoreFocus === true,
    });
    this.releaseModal(levelNumber);
    this.choiceModalState = {
      levelNumber: null,
      mode: 'new',
      options: [],
      currentSelection: null,
      selection: null,
      status: 'idle',
      opener: null,
      retryAction: null,
    };
    this.choiceSelectionExplicit = false;
    this.syncAcceleratedCheckoutAvailability();

    if (
      (reason === 'resolved' || reason === 'unavailable') &&
      this.checkoutIntent
    ) {
      this.openNextPendingForCheckout();
    } else if (reason === 'resolved') {
      this.scheduleNextPendingChoice();
    }

    return {
      reason,
      restoreFocus: restoreFocus === true,
    };
  }

  async resolveLevelOptions(
    levelNumber,
    { forceRefresh = false } = {},
  ) {
    const normalizedLevel = normalizeGiftChoiceLevelNumber(levelNumber);
    const levels = Array.isArray(this.config.levels)
      ? this.config.levels
      : [];
    const configuredLevel = levels.find((level, index) => (
      normalizeGiftChoiceLevelNumber(
        level && (level.levelNumber || index + 1),
      ) === normalizedLevel
    ));

    if (
      normalizedLevel === null ||
      !configuredLevel ||
      !this.productResolver ||
      typeof this.productResolver.resolveConfigured !== 'function'
    ) {
      return [];
    }

    const resolver = typeof this.productResolver.resolveChoiceOptions === 'function'
      ? this.productResolver.resolveChoiceOptions.bind(this.productResolver)
      : this.productResolver.resolveConfigured.bind(this.productResolver);
    const resolvedOptions = await resolver(
      configuredLevel.availableGifts,
      { fresh: forceRefresh === true },
    );
    const normalizedOptions = (Array.isArray(resolvedOptions)
      ? resolvedOptions
      : []
    ).map((option) => ({
      variantId: String(option.variantId),
      productHandle: option.productHandle || option.handle || '',
      title: typeof option.title === 'string' ? option.title : '',
      imageUrl: option.imageUrl || option.image || '',
      available: option.available === true,
    }));

    this.choiceResolvedOptionsByLevel.set(
      normalizedLevel,
      cloneGiftChoiceValue(resolvedOptions),
    );

    return normalizedOptions;
  }

  async commitGiftDecision(levelNumber, variantId) {
    const normalizedLevel = normalizeGiftChoiceLevelNumber(levelNumber);
    const levels = (
      this.lastResolution &&
      Array.isArray(this.lastResolution.levels)
    )
      ? this.lastResolution.levels
      : [];
    const resolvedLevel = levels.find(
      (level) => level.levelNumber === normalizedLevel,
    );
    const configuredLevels = Array.isArray(this.config.levels)
      ? this.config.levels
      : [];
    const configuredLevel = configuredLevels.find((level, index) => (
      normalizeGiftChoiceLevelNumber(
        level && (level.levelNumber || index + 1),
      ) === normalizedLevel
    ));
    const level = {
      ...(configuredLevel || {}),
      ...(resolvedLevel || {}),
      levelNumber: normalizedLevel,
    };
    const resolvedOptions = this.choiceResolvedOptionsByLevel.get(
      normalizedLevel,
    ) || level.viableOptions || [];
    const option = resolvedOptions.find((candidate) => (
      candidate &&
      String(candidate.variantId) === String(variantId)
    ));

    if (
      normalizedLevel === null ||
      !option ||
      !this.cartMutator
    ) {
      throw new TypeError('Decisão de brinde inválida');
    }

    const oldLine = Array.isArray(level.giftLines)
      ? level.giftLines[0] || null
      : null;
    let result;

    if (
      oldLine &&
      String(oldLine.variant_id) !== String(option.variantId) &&
      typeof this.cartMutator.swapGift === 'function'
    ) {
      result = await this.cartMutator.swapGift({
        level,
        oldLine,
        newOption: option,
      });
    } else if (typeof this.cartMutator.addGift === 'function') {
      result = await this.cartMutator.addGift({
        level,
        option,
        mode: 'choice',
      });
    } else {
      throw new TypeError('Mutador indisponível');
    }

    if (
      result &&
      result.status === 'gift' &&
      !(result.error && result.error.recoverable === true) &&
      isConfirmedGiftCart(result.cart)
    ) {
      this.clearTransientUnavailableFromRetry(normalizedLevel);
      const snapshot = await this.resolveCartSnapshot(
        result.cart,
        { source: 'gift-system' },
      );

      this.publish(snapshot);
    }

    return result;
  }

  async commitNoneDecision(levelNumber) {
    const normalizedLevel = normalizeGiftChoiceLevelNumber(levelNumber);
    let cart = this.lastConfirmedCart;
    const levels = (
      this.lastResolution &&
      Array.isArray(this.lastResolution.levels)
    )
      ? this.lastResolution.levels
      : [];
    const level = levels.find(
      (candidate) => candidate.levelNumber === normalizedLevel,
    );

    if (
      normalizedLevel === null ||
      !isConfirmedGiftCart(cart) ||
      !level ||
      typeof level.signature !== 'string'
    ) {
      throw new TypeError('Decisão sem brinde inválida');
    }

    const giftLines = Array.isArray(level.giftLines)
      ? level.giftLines
      : [];

    if (giftLines.length > 0) {
      if (
        !this.cartMutator ||
        typeof this.cartMutator.removeGift !== 'function'
      ) {
        throw new TypeError('Mutador de remoção indisponível');
      }

      const removal = await this.cartMutator.removeGift({
        level,
        oldLine: giftLines[0],
      });

      if (
        !removal ||
        removal.status !== 'removed' ||
        !isConfirmedGiftCart(removal.cart)
      ) {
        return removal;
      }

      cart = cloneGiftChoiceValue(removal.cart);
    }

    this.decisionStore.setNone({
      cartToken: cart.token,
      levelNumber: normalizedLevel,
      signature: level.signature,
      decision: 'none',
    });
    this.clearTransientUnavailableFromRetry(normalizedLevel);

    const snapshot = await this.resolveCartSnapshot(
      cart,
      { source: 'gift-system' },
    );

    this.publish(snapshot);

    return {
      status: 'none',
      cart: cloneGiftChoiceValue(cart),
      snapshot: cloneGiftChoiceValue(snapshot),
    };
  }

  async retryLevel(levelNumber) {
    const normalizedLevel = normalizeGiftChoiceLevelNumber(levelNumber);

    if (normalizedLevel === null) {
      return null;
    }

    this.clearTransientUnavailableFromRetry(normalizedLevel);

    let cart = null;

    if (
      this.cartGateway &&
      typeof this.cartGateway.getCart === 'function'
    ) {
      cart = await this.cartGateway.getCart();
    } else if (isConfirmedGiftCart(this.lastConfirmedCart)) {
      cart = cloneGiftChoiceValue(this.lastConfirmedCart);
    }

    if (!cart) {
      return null;
    }

    const snapshot = await this.resolveCartSnapshot(
      cart,
      { source: 'gift-system' },
    );

    this.publish(snapshot);
    return snapshot;
  }

  setTransientUnavailable(levelNumber, errorCode, cart) {
    const normalizedLevel = normalizeGiftChoiceLevelNumber(levelNumber);

    if (normalizedLevel === null) {
      return null;
    }

    const confirmedCart = isConfirmedGiftCart(cart)
      ? cloneGiftChoiceValue(cart)
      : null;
    const state = {
      status: 'unavailable',
      transient: true,
      cart: confirmedCart,
      error: {
        recoverable: true,
        code: typeof errorCode === 'string'
          ? errorCode
          : 'gift_state_ambiguous',
      },
      canRetry: true,
    };

    this.transientUnavailableByLevel.set(normalizedLevel, state);
    this.pendingByLevel.delete(normalizedLevel);
    this.syncAcceleratedCheckoutAvailability();

    if (confirmedCart) {
      this.lastConfirmedCart = cloneGiftChoiceValue(confirmedCart);
    }

    if (this.modalOwner === normalizedLevel) {
      this.modalOwner = null;
    }

    return cloneGiftChoiceValue(state);
  }

  reportGiftChoiceFailure({
    code,
    levelNumber,
    recoverable,
  } = {}) {
    const normalizedCode = (
      typeof code === 'string' &&
      /^[a-z0-9][a-z0-9:_-]{0,79}$/iu.test(code)
    )
      ? code
      : 'gift_choice_unavailable';
    const payload = {
      event: 'gift_choice_unavailable',
      code: normalizedCode,
      level_number: normalizeGiftChoiceLevelNumber(levelNumber),
      recoverable: recoverable === true,
    };
    const diagnostic = cloneGiftChoiceValue(payload);

    try {
      if (typeof this.logger === 'function') {
        this.logger(diagnostic);
      } else if (
        this.logger &&
        typeof this.logger.warn === 'function'
      ) {
        this.logger.warn(diagnostic);
      }
    } catch (error) {
      // Diagnostico e sempre best-effort.
    }

    try {
      if (this.gtag) {
        this.gtag('event', payload.event, {
          code: payload.code,
          level_number: payload.level_number,
          recoverable: payload.recoverable,
        });
      }
    } catch (error) {
      // Analytics nunca altera o terminal.
    }

    try {
      if (this.fbq) {
        this.fbq('trackCustom', 'GiftChoiceUnavailable', {
          event: payload.event,
          code: payload.code,
          level_number: payload.level_number,
          recoverable: payload.recoverable,
        });
      }
    } catch (error) {
      // Analytics nunca altera o terminal.
    }

    return payload;
  }

  failOpenLevel({
    levelNumber,
    code,
    cart = null,
    error = null,
  } = {}) {
    const normalizedLevel = normalizeGiftChoiceLevelNumber(levelNumber);

    if (normalizedLevel === null) {
      return null;
    }

    const hadModalOwnership = this.modalOwner === normalizedLevel;
    const state = this.setTransientUnavailable(
      normalizedLevel,
      code,
      cart,
    );

    if (!state) {
      return null;
    }

    const previousSnapshot = isGiftChoiceRecord(this.lastResolution)
      ? cloneGiftChoiceValue(this.lastResolution)
      : {};
    const previousLevels = Array.isArray(previousSnapshot.levels)
      ? previousSnapshot.levels
      : [];
    let replaced = false;
    const levels = previousLevels.map((level) => {
      if (
        !level ||
        Number(level.levelNumber) !== normalizedLevel
      ) {
        return level;
      }

      replaced = true;
      return {
        ...level,
        status: 'unavailable',
        action: null,
        option: null,
        transient: true,
        canRetry: true,
        error: cloneGiftChoiceValue(state.error),
      };
    });

    if (!replaced) {
      levels.push({
        levelNumber: normalizedLevel,
        status: 'unavailable',
        action: null,
        option: null,
        transient: true,
        canRetry: true,
        error: cloneGiftChoiceValue(state.error),
      });
    }

    const snapshot = {
      ...previousSnapshot,
      cart: isConfirmedGiftCart(state.cart)
        ? cloneGiftChoiceValue(state.cart)
        : (
          isConfirmedGiftCart(previousSnapshot.cart)
            ? cloneGiftChoiceValue(previousSnapshot.cart)
            : null
        ),
      levels,
      pendingLevels: this.replacePendingLevels(levels),
      effects: Array.isArray(previousSnapshot.effects)
        ? previousSnapshot.effects
        : [],
    };

    this.lastResolution = cloneGiftChoiceValue(snapshot);
    this.reportGiftChoiceFailure({
      code: state.error.code,
      levelNumber: normalizedLevel,
      recoverable: true,
      error,
    });
    this.publish(snapshot, {
      schedule: !hadModalOwnership,
    });

    return cloneGiftChoiceValue(state);
  }

  clearTransientUnavailableFromRetry(levelNumber) {
    const normalizedLevel = normalizeGiftChoiceLevelNumber(levelNumber);

    return normalizedLevel === null
      ? false
      : this.transientUnavailableByLevel.delete(normalizedLevel);
  }

  clearTransientUnavailableFromExternalCart(levelNumber, cart) {
    const normalizedLevel = normalizeGiftChoiceLevelNumber(levelNumber);

    if (
      normalizedLevel === null ||
      !isConfirmedGiftCart(cart)
    ) {
      return false;
    }

    const lines = getGiftChoiceLinesForLevel(cart, normalizedLevel);

    if (lines.length > 1) {
      return false;
    }

    return this.transientUnavailableByLevel.delete(normalizedLevel);
  }

  acceptConfirmedCart(cart) {
    if (!isConfirmedGiftCart(cart)) {
      return null;
    }

    this.lastConfirmedCart = cloneGiftChoiceValue(cart);

    const snapshot = this.lastResolution
      ? {
        ...cloneGiftChoiceValue(this.lastResolution),
        cart: cloneGiftChoiceValue(cart),
      }
      : {
        cart: cloneGiftChoiceValue(cart),
        levels: [],
        pendingLevels: Array.from(this.pendingByLevel.values()),
        effects: [],
      };

    this.lastResolution = cloneGiftChoiceValue(snapshot);
    this.publish(snapshot);

    return snapshot;
  }

  publish(snapshot, { schedule = true } = {}) {
    this.refreshChoiceSurfaceActions(snapshot);
    this.syncAcceleratedCheckoutAvailability();

    this.presenters.forEach((presenter) => {
      if (
        !presenter ||
        typeof presenter.renderGiftCoordinatorState !== 'function'
      ) {
        return;
      }

      try {
        presenter.renderGiftCoordinatorState(snapshot);
      } catch (error) {
        // Um presenter defeituoso não interrompe os demais.
      }
    });

    if (schedule) {
      this.scheduleNextPendingChoice();
    }

    return snapshot;
  }

  scheduleNextPendingChoice() {
    if (
      !this.config ||
      this.config.choiceEnabled !== true
    ) {
      return Promise.resolve(null);
    }

    if (this.checkoutIntent) {
      return this.openNextPendingForCheckout();
    }

    if (this.modalOwner !== null) {
      return Promise.resolve(null);
    }

    if (this.choicePromptPromise) {
      return this.choicePromptPromise;
    }

    const prompt = Promise.resolve().then(() => {
      if (this.modalOwner !== null) {
        return null;
      }

      const nextPending = Array.from(this.pendingByLevel.values()).find(
        (level) => (
          level &&
          level.status === 'pending' &&
          !this.dismissedChoiceLevels.has(level.levelNumber)
        ),
      );

      if (!nextPending) {
        return null;
      }

      return this.openChoiceModal({
        levelNumber: nextPending.levelNumber,
        mode: 'new',
      });
    });

    let wrappedPrompt = null;

    wrappedPrompt = prompt.finally(() => {
      if (this.choicePromptPromise === wrappedPrompt) {
        this.choicePromptPromise = null;
      }
    });
    this.choicePromptPromise = wrappedPrompt;

    return wrappedPrompt;
  }

  replacePendingLevels(levels) {
    const uniqueByLevel = new Map();

    if (Array.isArray(levels)) {
      levels.forEach((level) => {
        const source = isGiftChoiceRecord(level)
          ? level
          : { levelNumber: level, status: 'pending' };
        const levelNumber = normalizeGiftChoiceLevelNumber(source.levelNumber);

        if (levelNumber === null || source.status !== 'pending') {
          return;
        }

        uniqueByLevel.set(levelNumber, {
          ...source,
          levelNumber,
          status: 'pending',
        });
      });
    }

    const sorted = this.core.sortPendingLevels(
      Array.from(uniqueByLevel.values()),
    );

    this.pendingByLevel = new Map(
      sorted.map((level) => [Number(level.levelNumber), level]),
    );

    return Array.from(this.pendingByLevel.values());
  }

  peekPendingLevel() {
    const first = this.pendingByLevel.values().next();
    return first.done ? null : first.value;
  }

  claimModal(levelNumber) {
    const normalizedLevel = normalizeGiftChoiceLevelNumber(levelNumber);

    if (normalizedLevel === null || this.modalOwner !== null) {
      return false;
    }

    this.modalOwner = normalizedLevel;
    return true;
  }

  releaseModal(levelNumber) {
    const normalizedLevel = normalizeGiftChoiceLevelNumber(levelNumber);

    if (
      normalizedLevel === null ||
      this.modalOwner !== normalizedLevel
    ) {
      return false;
    }

    this.modalOwner = null;
    return true;
  }

  requestReconciliation(input, reconcileFn) {
    this.latestReconcileInput = input;
    this.latestReconcileFn = typeof reconcileFn === 'function'
      ? reconcileFn
      : (latestInput) => this.reconcileInput(latestInput);

    if (this.reconcilePromise) {
      this.reconcileAgain = true;
      return this.reconcilePromise;
    }

    const run = async () => {
      let pass = 0;
      let result = null;

      try {
        do {
          this.reconcileAgain = false;
          const currentInput = this.latestReconcileInput;
          const currentReconcileFn = this.latestReconcileFn;

          result = await currentReconcileFn(currentInput);

          if (result != null) {
            this.publish(result);
          }

          pass += 1;

          if (
            this.reconcileAgain &&
            isGiftChoiceRecord(result) &&
            isConfirmedGiftCart(result.cart) &&
            isGiftChoiceRecord(this.latestReconcileInput) &&
            !isConfirmedGiftCart(this.latestReconcileInput.cart)
          ) {
            this.latestReconcileInput = {
              ...this.latestReconcileInput,
              cart: cloneGiftChoiceValue(result.cart),
            };
          }
        } while (this.reconcileAgain && pass < 2);

        return result;
      } finally {
        this.reconcileAgain = false;
        this.reconcilePromise = null;
      }
    };

    this.reconcilePromise = run();
    return this.reconcilePromise;
  }

  runMutation(operationKey, mutationFn) {
    if (
      typeof operationKey !== 'string' ||
      operationKey.length === 0 ||
      typeof mutationFn !== 'function'
    ) {
      return Promise.reject(new TypeError('Mutação inválida'));
    }

    if (this.mutationByKey.has(operationKey)) {
      return this.mutationByKey.get(operationKey);
    }

    const operation = this.mutationTail.then(() => mutationFn());
    const release = () => {
      if (this.mutationByKey.get(operationKey) === operation) {
        this.mutationByKey.delete(operationKey);
      }
    };

    this.mutationByKey.set(operationKey, operation);
    operation.then(release, release);
    this.mutationTail = operation.then(
      () => undefined,
      () => undefined,
    );

    return operation;
  }

  markCoordinatedRemoval(lineKey) {
    if (typeof lineKey !== 'string' || lineKey.length === 0) {
      return false;
    }

    this.coordinatedRemovalKeys.add(lineKey);
    return true;
  }

  clearCoordinatedRemoval(lineKey) {
    return this.coordinatedRemovalKeys.delete(lineKey);
  }

  clearEphemeralLifecycleState() {
    this.cancelCheckoutIntent();
    this.replacePendingLevels([]);
    this.eligibilityByLevel.clear();
    this.coordinatedRemovalKeys.clear();
    this.transientByLevel.clear();
    this.transientUnavailableByLevel.clear();
    this.dismissedChoiceLevels.clear();
    this.modalOwner = null;
  }

  async reconcileInput(input) {
    if (
      isGiftChoiceRecord(input) &&
      Array.isArray(input.resolvedLevels)
    ) {
      return this.reconcileLifecycle(input);
    }

    let cart = (
      isGiftChoiceRecord(input) &&
      isConfirmedGiftCart(input.cart)
    )
      ? cloneGiftChoiceValue(input.cart)
      : null;
    let source = (
      isGiftChoiceRecord(input) &&
      typeof input.source === 'string'
    )
      ? input.source
      : 'external';

    if (
      source !== 'gift-system' &&
      source !== 'initial' &&
      this.mutationByKey.size > 0
    ) {
      await this.mutationTail;

      if (this.cartGateway) {
        const latestSource = (
          isGiftChoiceRecord(this.latestReconcileInput) &&
          typeof this.latestReconcileInput.source === 'string'
        )
          ? this.latestReconcileInput.source
          : source;

        source = latestSource;
        cart = null;
        this.reconcileAgain = false;
      }
    }

    if (!cart && this.cartGateway) {
      if (!this.externalFetchPromise) {
        this.externalFetchPromise = this.mutationTail
          .then(() => this.cartGateway.getCart())
          .finally(() => {
            this.externalFetchPromise = null;
          });
      }

      cart = await this.externalFetchPromise;
    }

    if (
      cart &&
      this.productResolver &&
      Array.isArray(this.config.levels)
    ) {
      return this.resolveCartSnapshot(cart, { source });
    }

    return {
      cart,
      unlockedLevels: [],
    };
  }

  async resolveCartSnapshot(cart, { source = 'external' } = {}) {
    if (
      !isConfirmedGiftCart(cart) ||
      !this.productResolver
    ) {
      return {
        cart: null,
        levels: [],
        pendingLevels: Array.from(this.pendingByLevel.values()),
        effects: [],
      };
    }

    const confirmedCart = cloneGiftChoiceValue(cart);

    if (source !== 'gift-system') {
      this.dismissedChoiceLevels.clear();
    }

    const cartTotal = getGiftChoiceCartTotal(confirmedCart);
    const configuredLevels = Array.isArray(this.config.levels)
      ? this.config.levels.filter((level, index) => (
        isGiftChoiceRecord(level) &&
        (index === 0 || level.enabled === true)
      ))
      : [];
    const resolvedLevels = await Promise.all(
      configuredLevels.map(async (configuredLevel, index) => {
        const levelNumber = normalizeGiftChoiceLevelNumber(
          configuredLevel.levelNumber || index + 1,
        );

        if (levelNumber === null) {
          return null;
        }

        const minValue = Number(configuredLevel.minValue);
        const eligible = (
          Number.isFinite(minValue) &&
          cartTotal >= minValue &&
          doesGiftChoiceTriggerMatch(
            confirmedCart,
            configuredLevel.triggerProducts,
          )
        );
        const viableOptions = await this.productResolver.resolveConfigured(
          configuredLevel.availableGifts,
        );
        const signature = this.core.buildOptionSignature(viableOptions);
        const giftLines = getGiftChoiceLinesForLevel(
          confirmedCart,
          levelNumber,
        );
        const decision = eligible
          ? this.decisionStore.getNone({
            cartToken: confirmedCart.token,
            levelNumber,
            signature,
          })
          : null;
        const resolution = this.core.resolveLevelState({
          levelNumber,
          eligible,
          options: viableOptions,
          cartItems: confirmedCart.items,
          decision,
        });

        return {
          ...cloneGiftChoiceValue(configuredLevel),
          ...resolution,
          levelNumber,
          eligible,
          signature,
          viableOptions: cloneGiftChoiceValue(viableOptions),
          giftLines: cloneGiftChoiceValue(giftLines),
        };
      }),
    );
    const lifecycle = this.reconcileLifecycle({
      cart: confirmedCart,
      resolvedLevels: resolvedLevels.filter(Boolean),
      source,
    });

    if (source !== 'gift-system' && source !== 'initial') {
      Array.from(this.transientUnavailableByLevel.keys()).forEach(
        (levelNumber) => {
          this.clearTransientUnavailableFromExternalCart(
            levelNumber,
            confirmedCart,
          );
        },
      );
    }

    lifecycle.levels.forEach((level) => {
      if (
        level &&
        level.eligible === true &&
        level.status === 'unavailable' &&
        !this.transientUnavailableByLevel.has(level.levelNumber)
      ) {
        this.failOpenLevel({
          levelNumber: level.levelNumber,
          code: 'gift_options_unavailable',
          cart: confirmedCart,
        });
      }
    });

    lifecycle.levels = lifecycle.levels.map((level) => {
      if (!this.transientUnavailableByLevel.has(level.levelNumber)) {
        return level;
      }

      return {
        ...level,
        status: 'unavailable',
        action: null,
        transient: true,
        canRetry: true,
        error: cloneGiftChoiceValue(
          this.transientUnavailableByLevel.get(level.levelNumber).error,
        ),
      };
    });
    lifecycle.pendingLevels = this.replacePendingLevels(lifecycle.levels);

    const effectResults = await this.executeLifecycleEffects(
      lifecycle.effects,
    );
    const confirmedEffects = effectResults.filter((result) => (
      result &&
      result.status === 'removed' &&
      isConfirmedGiftCart(result.cart)
    ));
    const lastConfirmedEffect = confirmedEffects.length > 0
      ? confirmedEffects[confirmedEffects.length - 1]
      : null;

    if (lastConfirmedEffect) {
      lifecycle.cart = cloneGiftChoiceValue(lastConfirmedEffect.cart);
      lifecycle.levels = lifecycle.levels.map((level) => ({
        ...level,
        giftLines: cloneGiftChoiceValue(
          getGiftChoiceLinesForLevel(
            lastConfirmedEffect.cart,
            level.levelNumber,
          ),
        ),
      }));
    }

    if (this.cartMutator) {
      for (const level of lifecycle.levels) {
        if (
          level.action === 'auto-add' &&
          level.option &&
          !this.transientUnavailableByLevel.has(level.levelNumber)
        ) {
          const mutationResult = await this.ensureGiftForLevel(level);

          if (
            mutationResult &&
            mutationResult.status === 'gift' &&
            isConfirmedGiftCart(mutationResult.cart)
          ) {
            lifecycle.cart = cloneGiftChoiceValue(mutationResult.cart);
            const giftLines = getGiftChoiceLinesForLevel(
              mutationResult.cart,
              level.levelNumber,
            );

            lifecycle.levels = lifecycle.levels.map(
              (currentLevel) => (
                currentLevel.levelNumber === level.levelNumber
                  ? {
                    ...currentLevel,
                    status: 'gift',
                    action: null,
                    option: null,
                    giftLines: cloneGiftChoiceValue(giftLines),
                    giftLineKey: giftLines[0]
                      ? giftLines[0].key || null
                      : null,
                  }
                  : currentLevel
              ),
            );
          } else if (
            mutationResult &&
            mutationResult.status === 'unavailable'
          ) {
            lifecycle.levels = lifecycle.levels.map(
              (currentLevel) => (
                currentLevel.levelNumber === level.levelNumber
                  ? {
                    ...currentLevel,
                    status: 'unavailable',
                    action: null,
                    option: null,
                    transient: mutationResult.transient === true,
                    canRetry: mutationResult.canRetry === true,
                    error: cloneGiftChoiceValue(mutationResult.error),
                  }
                  : currentLevel
              ),
            );
          }
        }
      }
    }

    lifecycle.pendingLevels = this.replacePendingLevels(lifecycle.levels);
    this.lastResolution = cloneGiftChoiceValue(lifecycle);
    return lifecycle;
  }

  ensureGiftForLevel(level) {
    if (
      !this.cartMutator ||
      typeof this.cartMutator.addGift !== 'function' ||
      !isGiftChoiceRecord(level) ||
      !level.option
    ) {
      return Promise.resolve(null);
    }

    return this.cartMutator.addGift({
      level,
      option: level.option,
      mode: 'auto',
    });
  }

  retrySwap(input) {
    const levelNumber = input && input.level
      ? input.level.levelNumber
      : null;

    this.clearTransientUnavailableFromRetry(levelNumber);

    if (
      !this.cartMutator ||
      typeof this.cartMutator.swapGift !== 'function'
    ) {
      return Promise.reject(new TypeError('Mutador indisponível'));
    }

    return this.cartMutator.swapGift(input);
  }

  async executeLifecycleEffects(effects) {
    if (
      !Array.isArray(effects) ||
      !this.cartGateway
    ) {
      return [];
    }

    const results = [];

    for (const effect of effects) {
      if (
        !isGiftChoiceRecord(effect) ||
        effect.type !== 'remove-gift' ||
        typeof effect.lineKey !== 'string'
      ) {
        continue;
      }

      const operationKey = `remove-invalid:${effect.lineKey}`;
      const result = await this.runMutation(operationKey, async () => {
        this.markCoordinatedRemoval(effect.lineKey);

        try {
          const mutation = await this.cartGateway.changeLine({
            lineKey: effect.lineKey,
            quantity: 0,
            role: 'remove-invalid',
          });
          let confirmedCart = isConfirmedGiftCart(mutation.cartCandidate)
            ? mutation.cartCandidate
            : await this.cartGateway.getCart();

          if (
            confirmedCart.items.some(
              (item) => item && item.key === effect.lineKey,
            )
          ) {
            confirmedCart = await this.cartGateway.getCart();
          }

          if (
            !isConfirmedGiftCart(confirmedCart) ||
            confirmedCart.items.some(
              (item) => item && item.key === effect.lineKey,
            )
          ) {
            throw createGiftCartError('gift_change_unconfirmed');
          }

          this.acceptConfirmedCart(confirmedCart);
          this.emit(cloneGiftChoiceValue(confirmedCart));

          return {
            status: 'removed',
            cart: cloneGiftChoiceValue(confirmedCart),
          };
        } catch (error) {
          return {
            status: 'unavailable',
            transient: true,
            error: {
              recoverable: true,
              code: error && error.code
                ? error.code
                : 'gift_change_failed',
            },
            canRetry: true,
          };
        } finally {
          this.clearCoordinatedRemoval(effect.lineKey);
        }
      });

      results.push(result);
    }

    return results;
  }

  reconcileLifecycle({
    cart,
    resolvedLevels,
    source = 'external',
  } = {}) {
    if (
      !isGiftChoiceRecord(cart) ||
      typeof cart.token !== 'string' ||
      cart.token.length === 0 ||
      !Array.isArray(cart.items) ||
      !Array.isArray(resolvedLevels)
    ) {
      return {
        cart: null,
        levels: [],
        pendingLevels: Array.from(this.pendingByLevel.values()),
        effects: [],
      };
    }

    const confirmedCart = cloneGiftChoiceValue(cart);
    const normalizedLevels = resolvedLevels
      .map(normalizeResolvedGiftChoiceLevel)
      .filter(Boolean);

    if (confirmedCart.items.length === 0) {
      this.decisionStore.clearAll();
      this.clearEphemeralLifecycleState();
      this.lastConfirmedCart = null;

      return {
        cart: confirmedCart,
        levels: [],
        pendingLevels: [],
        effects: [],
      };
    }

    const previousCart = this.lastConfirmedCart;
    const tokenChanged = (
      previousCart &&
      previousCart.token !== confirmedCart.token
    );

    this.decisionStore.retainOnlyCart(confirmedCart.token);

    if (tokenChanged) {
      this.clearEphemeralLifecycleState();
      this.lastConfirmedCart = null;
    }

    const activePreviousCart = tokenChanged ? null : previousCart;
    const currentLevelByNumber = new Map(
      normalizedLevels.map((level) => [level.levelNumber, level]),
    );
    const currentGiftKeys = new Set(
      confirmedCart.items
        .filter(isGiftChoiceLine)
        .map((item) => item.key)
        .filter((lineKey) => typeof lineKey === 'string'),
    );

    if (activePreviousCart && Array.isArray(activePreviousCart.items)) {
      activePreviousCart.items
        .filter(isGiftChoiceLine)
        .forEach((previousGift) => {
          if (currentGiftKeys.has(previousGift.key)) {
            return;
          }

          const levelNumber = getGiftChoiceLineLevel(previousGift);
          const currentLevel = currentLevelByNumber.get(levelNumber);

          if (
            this.coordinatedRemovalKeys.has(previousGift.key)
          ) {
            this.clearCoordinatedRemoval(previousGift.key);
            return;
          }

          if (currentLevel && currentLevel.eligible) {
            this.decisionStore.setNone({
              cartToken: confirmedCart.token,
              levelNumber,
              signature: currentLevel.signature,
              decision: 'none',
            });
          }
        });
    }

    const effects = [];
    const levels = normalizedLevels.map((level) => {
      const currentGiftLines = confirmedCart.items.filter(
        (item) => getGiftChoiceLineLevel(item) === level.levelNumber,
      );
      const previousEligibility = this.eligibilityByLevel.get(
        level.levelNumber,
      );
      const decisionScope = {
        cartToken: confirmedCart.token,
        levelNumber: level.levelNumber,
        signature: level.signature,
      };

      if (!level.eligible) {
        this.decisionStore.clearLevel(decisionScope);
        this.pendingByLevel.delete(level.levelNumber);
        this.transientByLevel.delete(level.levelNumber);

        currentGiftLines.forEach((giftLine) => {
          if (typeof giftLine.key !== 'string') {
            return;
          }

          this.markCoordinatedRemoval(giftLine.key);
          effects.push({
            type: 'remove-gift',
            lineKey: giftLine.key,
            levelNumber: level.levelNumber,
          });
        });
      } else if (
        previousEligibility &&
        (
          previousEligibility.eligible === false ||
          previousEligibility.signature !== level.signature
        )
      ) {
        this.decisionStore.clearLevel(decisionScope);
      }

      let decision = level.eligible
        ? this.decisionStore.getNone(decisionScope)
        : null;

      if (level.eligible && decision === null) {
        this.decisionStore.clearLevel(decisionScope);
      }

      const resolution = this.core.resolveLevelState({
        levelNumber: level.levelNumber,
        eligible: level.eligible,
        options: level.viableOptions,
        cartItems: confirmedCart.items,
        decision,
      });

      if (resolution.clearDecision) {
        this.decisionStore.clearLevel(decisionScope);
        decision = null;
      }

      this.eligibilityByLevel.set(level.levelNumber, {
        eligible: level.eligible,
        signature: level.signature,
      });

      return {
        ...level,
        ...resolution,
        viableOptions: cloneGiftChoiceValue(level.viableOptions),
        giftLines: cloneGiftChoiceValue(currentGiftLines),
      };
    });

    const pendingLevels = this.replacePendingLevels(levels);

    this.lastConfirmedCart = cloneGiftChoiceValue(confirmedCart);

    return {
      cart: cloneGiftChoiceValue(confirmedCart),
      levels: cloneGiftChoiceValue(levels),
      pendingLevels: cloneGiftChoiceValue(pendingLevels),
      effects: cloneGiftChoiceValue(effects),
    };
  }
}

class GiftCartMutator {
  constructor({
    gateway,
    productResolver,
    coordinator,
    emitCartUpdate = () => {},
  } = {}) {
    if (
      !gateway ||
      typeof gateway.getCart !== 'function' ||
      typeof gateway.addLine !== 'function' ||
      typeof gateway.changeLine !== 'function'
    ) {
      throw new TypeError('GiftCartGateway inválido');
    }

    if (
      !productResolver ||
      typeof productResolver.revalidateSelection !== 'function'
    ) {
      throw new TypeError('GiftProductResolver inválido');
    }

    if (
      !coordinator ||
      typeof coordinator.runMutation !== 'function'
    ) {
      throw new TypeError('GiftChoiceCoordinator inválido');
    }

    this.gateway = gateway;
    this.productResolver = productResolver;
    this.coordinator = coordinator;
    this.emitCartUpdate = typeof emitCartUpdate === 'function'
      ? emitCartUpdate
      : () => {};
  }

  createUnavailable(levelNumber, code, cart, error = null) {
    return this.coordinator.failOpenLevel({
      levelNumber,
      code,
      cart,
      error,
    });
  }

  createRecoverableError(
    code,
    cart,
    {
      variantId,
      optionUnavailable = false,
    } = {},
  ) {
    const confirmedCart = isConfirmedGiftCart(cart)
      ? cloneGiftChoiceValue(cart)
      : null;

    if (confirmedCart) {
      this.coordinator.acceptConfirmedCart(confirmedCart);
    }

    return {
      status: 'error',
      cart: confirmedCart,
      error: {
        recoverable: true,
        code: typeof code === 'string' && code.length > 0
          ? code
          : 'gift_choice_failed',
        optionUnavailable: optionUnavailable === true,
        variantId: variantId == null ? '' : String(variantId),
      },
      canRetry: true,
    };
  }

  confirmCart(cart, { emit = true } = {}) {
    if (!isConfirmedGiftCart(cart)) {
      return null;
    }

    this.coordinator.acceptConfirmedCart(cart);

    if (emit) {
      this.emitCartUpdate(cloneGiftChoiceValue(cart));
    }

    return cloneGiftChoiceValue(cart);
  }

  async confirmUnbudgeted(cartCandidate, predicate) {
    if (
      isConfirmedGiftCart(cartCandidate) &&
      predicate(cartCandidate)
    ) {
      return {
        matched: true,
        cart: cloneGiftChoiceValue(cartCandidate),
      };
    }

    let lastCart = isConfirmedGiftCart(cartCandidate)
      ? cloneGiftChoiceValue(cartCandidate)
      : null;

    for (let attempt = 0; attempt < 2; attempt += 1) {
      const cart = await this.gateway.getCart();

      lastCart = cloneGiftChoiceValue(cart);

      if (predicate(cart)) {
        return {
          matched: true,
          cart: lastCart,
        };
      }

      if (attempt === 0) {
        await this.gateway.wait(250);
      }
    }

    return {
      matched: false,
      cart: lastCart,
    };
  }

  addGift({ level, option, mode = 'choice' } = {}) {
    const levelNumber = normalizeGiftChoiceLevelNumber(
      level && level.levelNumber,
    );

    if (levelNumber === null || !isGiftChoiceRecord(option)) {
      return Promise.reject(new TypeError('Brinde inválido'));
    }

    return this.coordinator.runMutation(
      `add:${levelNumber}`,
      () => this.performAddGift({
        level: cloneGiftChoiceValue(level),
        option: cloneGiftChoiceValue(option),
        mode: mode === 'auto' ? 'auto' : 'choice',
      }),
    );
  }

  removeGift({ level, oldLine } = {}) {
    const levelNumber = normalizeGiftChoiceLevelNumber(
      level && level.levelNumber,
    );

    if (
      levelNumber === null ||
      !isGiftChoiceLine(oldLine)
    ) {
      return Promise.reject(new TypeError('Remoção de brinde inválida'));
    }

    return this.coordinator.runMutation(
      `decline:${levelNumber}`,
      () => this.performRemoveGift({
        level: cloneGiftChoiceValue(level),
        oldLine: cloneGiftChoiceValue(oldLine),
      }),
    );
  }

  async performRemoveGift({ level, oldLine }) {
    const levelNumber = level.levelNumber;
    let preflight;

    try {
      preflight = await this.gateway.getCart();
    } catch (error) {
      return this.createUnavailable(
        levelNumber,
        error && error.code ? error.code : 'cart_fetch_failed',
        this.coordinator.lastConfirmedCart,
      );
    }

    const giftLines = getGiftChoiceLinesForLevel(
      preflight,
      levelNumber,
    );

    if (giftLines.length === 0) {
      this.confirmCart(preflight, { emit: false });

      return {
        status: 'removed',
        cart: cloneGiftChoiceValue(preflight),
        error: null,
      };
    }

    if (
      giftLines.length !== 1 ||
      giftLines[0].key !== oldLine.key
    ) {
      return this.createUnavailable(
        levelNumber,
        'gift_state_ambiguous',
        preflight,
      );
    }

    let mutation = null;
    let errorCode = 'gift_remove_failed';

    this.coordinator.markCoordinatedRemoval(oldLine.key);

    try {
      mutation = await this.gateway.changeLine({
        lineKey: oldLine.key,
        quantity: 0,
        role: 'decline',
      });
    } catch (error) {
      errorCode = error && error.code
        ? error.code
        : 'gift_remove_failed';
    }

    let checkpoint;

    try {
      checkpoint = await this.confirmUnbudgeted(
        mutation ? mutation.cartCandidate : null,
        (cart) => (
          getGiftChoiceLinesForLevel(cart, levelNumber).length === 0
        ),
      );
    } catch (error) {
      this.coordinator.clearCoordinatedRemoval(oldLine.key);

      return this.createUnavailable(
        levelNumber,
        error && error.code ? error.code : errorCode,
        preflight,
      );
    }

    this.coordinator.clearCoordinatedRemoval(oldLine.key);

    if (!checkpoint.matched) {
      return this.createUnavailable(
        levelNumber,
        'gift_remove_unconfirmed',
        checkpoint.cart,
      );
    }

    this.confirmCart(checkpoint.cart);

    return {
      status: 'removed',
      cart: cloneGiftChoiceValue(checkpoint.cart),
      error: null,
    };
  }

  async performAddGift({ level, option, mode }) {
    const levelNumber = level.levelNumber;
    const recoverInModal = mode !== 'auto';
    let revalidated;

    try {
      revalidated = await this.productResolver.revalidateSelection(option);
    } catch (error) {
      if (recoverInModal) {
        return this.createRecoverableError(
          error && error.code ? error.code : 'product_fetch_failed',
          this.coordinator.lastConfirmedCart,
          {
            variantId: option.variantId,
            optionUnavailable: false,
          },
        );
      }

      return this.createUnavailable(
        levelNumber,
        error && error.code ? error.code : 'product_fetch_failed',
        this.coordinator.lastConfirmedCart,
      );
    }

    if (!revalidated.available) {
      if (recoverInModal) {
        return this.createRecoverableError(
          revalidated.reason,
          this.coordinator.lastConfirmedCart,
          {
            variantId: option.variantId,
            optionUnavailable: revalidated.reason !== 'product_fetch_failed',
          },
        );
      }

      return this.createUnavailable(
        levelNumber,
        revalidated.reason,
        this.coordinator.lastConfirmedCart,
      );
    }

    const selectedOption = revalidated.option;
    let preflight;

    try {
      preflight = await this.gateway.getCart();
    } catch (error) {
      return this.createUnavailable(
        levelNumber,
        error && error.code ? error.code : 'cart_fetch_failed',
        this.coordinator.lastConfirmedCart,
      );
    }

    const existingLines = getGiftChoiceLinesForLevel(
      preflight,
      levelNumber,
    );

    if (
      existingLines.length === 1 &&
      isExactGiftChoiceLine(existingLines[0], selectedOption.variantId)
    ) {
      this.coordinator.acceptConfirmedCart(preflight);

      return {
        status: 'gift',
        gift: 'new',
        cart: cloneGiftChoiceValue(preflight),
        error: null,
      };
    }

    if (existingLines.length > 0) {
      const convergence = await this.convergeLevelToSingleGift({
        cart: preflight,
        levelNumber,
        preferredVariantId: selectedOption.variantId,
      });

      if (convergence.status === 'gift') {
        this.confirmCart(convergence.cart);
      }

      return convergence;
    }

    const properties = {
      _is_gift: 'true',
      _gift_level: String(levelNumber),
      _gift_discount_percent: String(
        Number.isFinite(Number(level.discountPercent))
          ? Number(level.discountPercent)
          : 100,
      ),
      _original_price: String(selectedOption.price ?? option.price ?? 0),
    };
    let mutation = null;
    let addErrorCode = 'gift_add_failed';

    try {
      mutation = await this.gateway.addLine({
        variantId: selectedOption.variantId,
        quantity: 1,
        properties,
        role: 'add',
      });
    } catch (error) {
      addErrorCode = error && error.code
        ? error.code
        : 'gift_add_failed';
    }

    let checkpoint;

    try {
      checkpoint = await this.confirmUnbudgeted(
        mutation ? mutation.cartCandidate : null,
        (cart) => {
          const lines = getGiftChoiceLinesForLevel(cart, levelNumber);

          return (
            lines.length === 1 &&
            isExactGiftChoiceLine(lines[0], selectedOption.variantId)
          );
        },
      );
    } catch (error) {
      return this.createUnavailable(
        levelNumber,
        error && error.code ? error.code : 'gift_add_unconfirmed',
        preflight,
      );
    }

    if (!checkpoint.matched) {
      const finalCart = isConfirmedGiftCart(checkpoint.cart)
        ? checkpoint.cart
        : null;
      const finalLines = finalCart
        ? getGiftChoiceLinesForLevel(finalCart, levelNumber)
        : [];

      if (recoverInModal && finalCart && finalLines.length === 0) {
        return this.createRecoverableError(
          mutation ? 'gift_add_unconfirmed' : addErrorCode,
          finalCart,
          {
            variantId: selectedOption.variantId,
            optionUnavailable: false,
          },
        );
      }

      return this.createUnavailable(
        levelNumber,
        'gift_add_unconfirmed',
        checkpoint.cart,
      );
    }

    this.confirmCart(checkpoint.cart);

    return {
      status: 'gift',
      gift: 'new',
      cart: cloneGiftChoiceValue(checkpoint.cart),
      error: null,
    };
  }

  async convergeLevelToSingleGift({
    cart,
    levelNumber,
    preferredVariantId,
  } = {}) {
    if (!isConfirmedGiftCart(cart)) {
      return this.createUnavailable(
        levelNumber,
        'gift_state_ambiguous',
        null,
      );
    }

    const lines = getGiftChoiceLinesForLevel(cart, levelNumber);

    if (lines.length === 0) {
      return this.createUnavailable(
        levelNumber,
        'gift_missing',
        cart,
      );
    }

    // Decisão (o que remover, o que corrigir) vem do núcleo único
    // (CART-07) — a mesma regra que GiftSystemManager.convergeGiftLineQuantities
    // (caminho legado) aplica. Aqui só executa via gateway.
    const core = this.coordinator.core;
    const plan = core.planGiftLineConvergence({ lines, preferredVariantId });

    try {
      for (const lineKey of plan.removeLineKeys) {
        await this.gateway.changeLine({
          lineKey,
          quantity: 0,
          role: 'converge-remove',
        });
      }

      if (plan.quantityFix) {
        await this.gateway.changeLine({
          lineKey: plan.quantityFix.lineKey,
          quantity: plan.quantityFix.quantity,
          role: 'converge-quantity',
        });
      }

      const confirmed = await this.gateway.getCart();
      const finalLines = getGiftChoiceLinesForLevel(
        confirmed,
        levelNumber,
      );

      if (
        finalLines.length !== 1 ||
        finalLines[0].quantity !== 1
      ) {
        return this.createUnavailable(
          levelNumber,
          'gift_convergence_failed',
          confirmed,
        );
      }

      return {
        status: 'gift',
        gift: String(finalLines[0].variant_id) === String(preferredVariantId)
          ? 'new'
          : 'old',
        cart: cloneGiftChoiceValue(confirmed),
        error: null,
      };
    } catch (error) {
      return this.createUnavailable(
        levelNumber,
        error && error.code ? error.code : 'gift_convergence_failed',
        cart,
      );
    }
  }

  createBudget() {
    return {
      cartGets: 0,
      removePosts: 0,
      addPosts: 0,
      restorePosts: 0,
    };
  }

  async getBudgetedCart(budget) {
    if (budget.cartGets >= 7) {
      throw createGiftCartError('gift_swap_budget_exceeded');
    }

    budget.cartGets += 1;
    return this.gateway.getCart();
  }

  async runBudgetedPost(role, budget, input) {
    const counterByRole = {
      remove: 'removePosts',
      add: 'addPosts',
      restore: 'restorePosts',
    };
    const counter = counterByRole[role];

    if (!counter || budget[counter] >= 1) {
      throw createGiftCartError('gift_swap_budget_exceeded');
    }

    budget[counter] += 1;

    if (role === 'remove') {
      return this.gateway.changeLine({
        ...input,
        role,
      });
    }

    return this.gateway.addLine({
      ...input,
      role,
    });
  }

  async confirmCheckpoint({
    cartCandidate,
    predicate,
    budget,
  }) {
    if (isConfirmedGiftCart(cartCandidate)) {
      const cart = cloneGiftChoiceValue(cartCandidate);
      let matched = false;

      try {
        matched = predicate(cart);
      } catch (error) {
        return {
          matched: false,
          cart,
          error,
        };
      }

      return {
        matched,
        cart,
      };
    }

    let lastCart = null;

    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        lastCart = await this.getBudgetedCart(budget);
      } catch (error) {
        return {
          matched: false,
          cart: lastCart,
          error,
        };
      }

      let matched = false;

      try {
        matched = predicate(lastCart);
      } catch (error) {
        return {
          matched: false,
          cart: cloneGiftChoiceValue(lastCart),
          error,
        };
      }

      if (matched) {
        return {
          matched: true,
          cart: cloneGiftChoiceValue(lastCart),
        };
      }

      if (attempt === 0) {
        try {
          await this.gateway.wait(250);
        } catch (error) {
          return {
            matched: false,
            cart: cloneGiftChoiceValue(lastCart),
            error,
          };
        }
      }
    }

    return {
      matched: false,
      cart: isConfirmedGiftCart(lastCart)
        ? cloneGiftChoiceValue(lastCart)
        : null,
    };
  }

  swapGift({ level, oldLine, newOption } = {}) {
    const levelNumber = normalizeGiftChoiceLevelNumber(
      level && level.levelNumber,
    );

    if (
      levelNumber === null ||
      !isGiftChoiceLine(oldLine) ||
      !isGiftChoiceRecord(newOption)
    ) {
      return Promise.reject(new TypeError('Troca inválida'));
    }

    return this.coordinator.runMutation(
      `swap:${levelNumber}`,
      () => this.performSwapGift({
        level: cloneGiftChoiceValue(level),
        oldLine: cloneGiftChoiceValue(oldLine),
        newOption: cloneGiftChoiceValue(newOption),
      }),
    );
  }

  async performSwapGift({ level, oldLine, newOption }) {
    const levelNumber = level.levelNumber;
    const budget = this.createBudget();
    let revalidated;

    try {
      revalidated = await this.productResolver.revalidateSelection(
        newOption,
      );
    } catch (error) {
      return this.createRecoverableError(
        error && error.code ? error.code : 'product_fetch_failed',
        this.coordinator.lastConfirmedCart,
        {
          variantId: newOption.variantId,
          optionUnavailable: false,
        },
      );
    }

    if (!revalidated.available) {
      return this.createRecoverableError(
        revalidated.reason,
        this.coordinator.lastConfirmedCart,
        {
          variantId: newOption.variantId,
          optionUnavailable: revalidated.reason !== 'product_fetch_failed',
        },
      );
    }

    const selectedOption = revalidated.option;
    let preflight;

    try {
      preflight = await this.getBudgetedCart(budget);
    } catch (error) {
      return this.createUnavailable(
        levelNumber,
        error.code || 'cart_fetch_failed',
        null,
      );
    }

    const preflightLines = getGiftChoiceLinesForLevel(
      preflight,
      levelNumber,
    );

    if (preflightLines.length > 1) {
      return this.createUnavailable(
        levelNumber,
        'gift_state_ambiguous',
        preflight,
      );
    }

    if (
      preflightLines.length === 1 &&
      isExactGiftChoiceLine(
        preflightLines[0],
        selectedOption.variantId,
      )
    ) {
      this.coordinator.acceptConfirmedCart(preflight);

      return {
        status: 'gift',
        gift: 'new',
        cart: cloneGiftChoiceValue(preflight),
        error: null,
      };
    }

    const confirmedOldLine = preflightLines.find(
      (line) => line.key === oldLine.key,
    );

    if (preflightLines.length === 1 && !confirmedOldLine) {
      return this.createUnavailable(
        levelNumber,
        'gift_state_ambiguous',
        preflight,
      );
    }

    let zeroCart = preflightLines.length === 0
      ? cloneGiftChoiceValue(preflight)
      : null;

    if (confirmedOldLine) {
      let removeMutation = null;
      let removeErrorCode = 'gift_remove_unconfirmed';

      this.coordinator.markCoordinatedRemoval(confirmedOldLine.key);

      try {
        removeMutation = await this.runBudgetedPost(
          'remove',
          budget,
          {
            lineKey: confirmedOldLine.key,
            quantity: 0,
          },
        );
      } catch (error) {
        removeErrorCode = error.code || 'gift_remove_failed';
      }

      const removeCheckpoint = await this.confirmCheckpoint({
        cartCandidate: removeMutation
          ? removeMutation.cartCandidate
          : null,
        predicate: (cart) => (
          getGiftChoiceLinesForLevel(cart, levelNumber).length === 0
        ),
        budget,
      });

      this.coordinator.clearCoordinatedRemoval(confirmedOldLine.key);

      if (!removeCheckpoint.matched) {
        const remainingLines = getGiftChoiceLinesForLevel(
          removeCheckpoint.cart,
          levelNumber,
        );
        const oldStillConfirmed = (
          remainingLines.length === 1 &&
          remainingLines[0].key === confirmedOldLine.key
        );

        if (oldStillConfirmed) {
          this.coordinator.acceptConfirmedCart(removeCheckpoint.cart);

          return {
            status: 'gift',
            gift: 'old',
            cart: cloneGiftChoiceValue(removeCheckpoint.cart),
            error: {
              recoverable: true,
              code: removeErrorCode,
            },
          };
        }

        return this.createUnavailable(
          levelNumber,
          'gift_remove_ambiguous',
          removeCheckpoint.cart,
        );
      }

      zeroCart = cloneGiftChoiceValue(removeCheckpoint.cart);
    }

    const newProperties = {
      _is_gift: 'true',
      _gift_level: String(levelNumber),
      _gift_discount_percent: String(
        Number.isFinite(Number(level.discountPercent))
          ? Number(level.discountPercent)
          : 100,
      ),
      _original_price: String(
        selectedOption.price ?? newOption.price ?? 0,
      ),
    };
    let addMutation = null;
    let addCheckpoint = null;
    let addErrorCode = 'gift_add_failed';

    try {
      addMutation = await this.runBudgetedPost(
        'add',
        budget,
        {
          variantId: selectedOption.variantId,
          quantity: 1,
          properties: newProperties,
        },
      );
    } catch (error) {
      addErrorCode = error.code || 'gift_add_failed';
    }

    addCheckpoint = await this.confirmCheckpoint({
      cartCandidate: addMutation
        ? addMutation.cartCandidate
        : null,
      predicate: (cart) => {
        const lines = getGiftChoiceLinesForLevel(cart, levelNumber);

        return (
          lines.length === 1 &&
          isExactGiftChoiceLine(lines[0], selectedOption.variantId)
        );
      },
      budget,
    });

    if (addCheckpoint.matched) {
      this.confirmCart(addCheckpoint.cart);

      return {
        status: 'gift',
        gift: 'new',
        cart: cloneGiftChoiceValue(addCheckpoint.cart),
        error: null,
      };
    }

    if (addCheckpoint.error) {
      return this.createUnavailable(
        levelNumber,
        addCheckpoint.error.code || addErrorCode,
        addCheckpoint.cart,
        addCheckpoint.error,
      );
    }

    if (addMutation) {
      addErrorCode = 'gift_add_unconfirmed';
    }

    const lastAfterAdd = addCheckpoint && isConfirmedGiftCart(
      addCheckpoint.cart,
    )
      ? addCheckpoint.cart
      : zeroCart;
    const zeroStillConfirmed = (
      isConfirmedGiftCart(lastAfterAdd) &&
      getGiftChoiceLinesForLevel(lastAfterAdd, levelNumber).length === 0
    );

    if (!zeroStillConfirmed) {
      return this.createUnavailable(
        levelNumber,
        addErrorCode,
        lastAfterAdd,
      );
    }

    if (!confirmedOldLine) {
      return this.createRecoverableError(
        addErrorCode,
        lastAfterAdd,
        {
          variantId: selectedOption.variantId,
          optionUnavailable: false,
        },
      );
    }

    let restoreMutation;
    let restoreErrorCode = 'gift_restore_unconfirmed';

    try {
      restoreMutation = await this.runBudgetedPost(
        'restore',
        budget,
        {
          variantId: oldLine.variant_id,
          quantity: 1,
          properties: clonePrivateGiftProperties(oldLine.properties),
        },
      );
    } catch (error) {
      restoreMutation = null;
      restoreErrorCode = error.code || 'gift_restore_failed';
    }

    const restoreCheckpoint = await this.confirmCheckpoint({
      cartCandidate: restoreMutation
        ? restoreMutation.cartCandidate
        : null,
      predicate: (cart) => {
        const lines = getGiftChoiceLinesForLevel(cart, levelNumber);

        return (
          lines.length === 1 &&
          isExactGiftChoiceLine(lines[0], oldLine.variant_id)
        );
      },
      budget,
    });

    if (restoreCheckpoint.matched) {
      this.confirmCart(restoreCheckpoint.cart);

      return {
        status: 'gift',
        gift: 'old',
        cart: cloneGiftChoiceValue(restoreCheckpoint.cart),
        error: {
          recoverable: true,
          code: addErrorCode,
        },
      };
    }

    return this.createUnavailable(
      levelNumber,
      restoreErrorCode,
      restoreCheckpoint.cart,
    );
  }
}

function getGiftChoiceRuntimeStorage(runtime) {
  try {
    return runtime && runtime.localStorage
      ? runtime.localStorage
      : null;
  } catch (error) {
    return null;
  }
}

function normalizeGiftCartPath(path) {
  if (typeof path !== 'string' || path.length === 0) {
    return '';
  }

  const withoutQuery = path.split(/[?#]/u, 1)[0];
  const normalized = withoutQuery.replace(/\/+$/u, '');

  return normalized || '/';
}

function refreshGiftCartSurface(runtime, cart) {
  if (!runtime || !isConfirmedGiftCart(cart)) {
    return null;
  }

  const locationRef = runtime.location || null;
  const pathname = normalizeGiftCartPath(
    locationRef && locationRef.pathname,
  );
  const configuredCartPath = normalizeGiftCartPath(
    runtime.routes && runtime.routes.cart,
  );
  const isCartPage = (
    pathname.length > 0 &&
    (
      pathname === configuredCartPath ||
      /(?:^|\/)cart$/u.test(pathname)
    )
  );

  if (
    isCartPage &&
    locationRef &&
    typeof locationRef.reload === 'function'
  ) {
    locationRef.reload();
    return 'cart-page';
  }

  const sharedFunctions = runtime.sharedFunctions;

  if (
    sharedFunctions &&
    typeof sharedFunctions.updateSidebarCart === 'function'
  ) {
    try {
      sharedFunctions.updateSidebarCart(
        cloneGiftChoiceValue(cart),
      );
      return 'sidebar';
    } catch (error) {
      // A confirmação do carrinho não pode falhar por erro de renderização.
    }
  }

  if (
    locationRef &&
    typeof locationRef.reload === 'function'
  ) {
    locationRef.reload();
    return 'reload-fallback';
  }

  return null;
}

function getGiftChoiceCoordinator(runtime, deps = {}) {
  if (!runtime || (typeof runtime !== 'object' && typeof runtime !== 'function')) {
    throw new TypeError('Runtime inválido');
  }

  const existing = runtime.GiftChoiceCoordinator;

  if (existing && existing.__giftChoiceCoordinator === true) {
    return existing;
  }

  const core = deps.core || runtime.GiftChoiceCore || GiftChoiceCoreDependency;
  const decisionStore = deps.decisionStore || new GiftDecisionStorageAdapter({
    storage: Object.prototype.hasOwnProperty.call(deps, 'storage')
      ? deps.storage
      : getGiftChoiceRuntimeStorage(runtime),
    memory: deps.memory || new Map(),
    core,
  });
  let cartGateway = deps.cartGateway || null;

  if (
    !cartGateway &&
    isGiftChoiceRecord(deps.routes || runtime.routes)
  ) {
    const runtimeFetch = deps.fetchImpl || (
      typeof runtime.fetch === 'function'
        ? runtime.fetch.bind(runtime)
        : null
    );

    if (runtimeFetch) {
      cartGateway = new GiftCartGateway({
        routes: deps.routes || runtime.routes,
        fetchImpl: runtimeFetch,
        wait: deps.wait,
      });
    }
  }

  const productResolver = deps.productResolver || (
    cartGateway
      ? new GiftProductResolver({
        gateway: cartGateway,
        core,
        cache: deps.productCache || new Map(),
      })
      : null
  );
  const emitCartUpdate = deps.emitCartUpdate || ((cart) => {
    const documentRef = runtime.document;
    const CustomEventConstructor = runtime.CustomEvent || (
      typeof CustomEvent === 'function' ? CustomEvent : null
    );

    if (
      documentRef &&
      typeof documentRef.dispatchEvent === 'function' &&
      CustomEventConstructor
    ) {
      documentRef.dispatchEvent(new CustomEventConstructor('cart-update', {
        detail: createGiftCartEventDetail(cart),
      }));
    }

    refreshGiftCartSurface(runtime, cart);
  });
  const coordinator = new GiftChoiceCoordinator({
    config: deps.config || runtime.giftSystemConfig || {},
    core,
    decisionStore,
    cartGateway,
    productResolver,
    emit: deps.emit || emitCartUpdate,
    logger: deps.logger || null,
    gtag: deps.gtag || runtime.gtag || null,
    fbq: deps.fbq || runtime.fbq || null,
    debug: deps.debug === true,
  });
  const cartMutator = deps.cartMutator || (
    cartGateway && productResolver
      ? new GiftCartMutator({
        gateway: cartGateway,
        productResolver,
        coordinator,
        emitCartUpdate,
      })
      : null
  );

  coordinator.setRuntimeDependencies({
    cartGateway,
    productResolver,
    cartMutator,
  });

  Object.defineProperty(coordinator, '__giftChoiceCoordinator', {
    value: true,
    enumerable: false,
    configurable: false,
    writable: false,
  });
  runtime.GiftChoiceCoordinator = coordinator;

  return coordinator;
}

function normalizeCartUpdateDetail(detail) {
  if (
    isGiftChoiceRecord(detail) &&
    isConfirmedGiftCart(detail.cart)
  ) {
    return {
      source: typeof detail.source === 'string' ? detail.source : 'theme',
      cart: cloneGiftChoiceValue(detail.cart),
    };
  }

  if (isConfirmedGiftCart(detail)) {
    return {
      source: 'theme',
      cart: cloneGiftChoiceValue(detail),
    };
  }

  return {
    source: (
      isGiftChoiceRecord(detail) &&
      typeof detail.source === 'string'
    )
      ? detail.source
      : 'theme',
    cart: null,
  };
}

function createGiftCartEventDetail(cart) {
  return {
    source: 'gift-system',
    cart: isConfirmedGiftCart(cart)
      ? cloneGiftChoiceValue(cart)
      : null,
  };
}

function normalizeGiftChoiceCartUpdate(event) {
  return normalizeCartUpdateDetail(event && event.detail);
}

function routeGiftChoiceCartUpdate(runtime, event) {
  const update = normalizeGiftChoiceCartUpdate(event);
  const coordinator = runtime && runtime.GiftChoiceCoordinator;

  if (
    !coordinator ||
    coordinator.__giftChoiceCoordinator !== true ||
    typeof coordinator.requestReconciliation !== 'function'
  ) {
    return null;
  }

  if (
    typeof coordinator.syncAcceleratedCheckoutAvailability === 'function'
  ) {
    coordinator.syncAcceleratedCheckoutAvailability();
  }

  if (update.source === 'gift-system') {
    return null;
  }

  return coordinator.requestReconciliation(update);
}

function attachGiftChoiceCartListener(runtime) {
  if (!runtime || runtime.giftChoiceCoordinatorListenersAttached === true) {
    return false;
  }

  const documentRef = runtime.document;

  if (!documentRef || typeof documentRef.addEventListener !== 'function') {
    return false;
  }

  documentRef.addEventListener('cart-update', (event) => {
    const reconciliation = routeGiftChoiceCartUpdate(runtime, event);

    if (reconciliation && typeof reconciliation.catch === 'function') {
      reconciliation.catch(() => {
        // Falha de reconciliação não quebra o barramento global.
      });
    }
  });
  runtime.giftChoiceCoordinatorListenersAttached = true;

  return true;
}

class GiftSystemManager extends GiftSystemElementBase {
  constructor() {
    super();

    this.runtime = GiftSystemRuntime;

    // Carregar configurações do settings
    this.config = this.runtime.giftSystemConfig || { enabled: false };
    this.levels = this.config.levels || [];


    // Validar configuração
    if (!this.config.enabled || this.levels.length === 0) {
      this.config.enabled = false;
    }

    // Estado atual
    this.currentCart = null;
    this.previousCart = null; // Para detectar remoções manuais
    this.unlockedLevels = [];
    this.addedGifts = [];
    this.isInitializing = false;
    this.isProcessing = false;
    this.cartUpdateTimeout = null;
    
    // Cache de produtos de brinde (otimização)
    this.productCache = new Map();

    // Elementos DOM (serão definidos no connectedCallback)
    this.progressBar = null;
    this.progressFill = null;
    this.progressMessage = null;
  }

  connectedCallback() {

    // Verificar se o sistema está habilitado
    if (!this.config.enabled) {
      return;
    }

    // Aguardar tema estar completamente carregado (window.onload)
    // Isso garante que o gift-system não interfere com inicialização do tema
    if (document.readyState === 'complete') {
      // Já carregou
      setTimeout(() => this.safeInitialize(), 200);
    } else {
      // Esperar load event
      window.addEventListener('load', () => {
        setTimeout(() => this.safeInitialize(), 200);
      });
    }
  }

  /**
   * Inicialização segura com tratamento de erros
   */
  async safeInitialize() {
    try {
      await this.initialize();
    } catch (error) {
      // Não deixar erro quebrar o resto da página
    }
  }

  /**
   * Inicializa o sistema
   */
  async initialize() {
    if (this.isInitializing || !this.config.enabled) {
      return;
    }

    // Prevenir múltiplas inicializações do mesmo elemento
    if (this.hasAttribute('data-initialized')) {
      if (this.config.choiceEnabled === true) {
        this.choiceCoordinator = this.choiceCoordinator ||
          getGiftChoiceCoordinator(this.runtime);
        this.choiceCoordinator.bindChoiceSurfaceActions();
        this.choiceCoordinator.installCheckoutGuard();
        this.choiceCoordinator.syncAcceleratedCheckoutAvailability();
        this.choiceCoordinator.registerPresenter(this);
        attachGiftChoiceCartListener(this.runtime);
      }
      return;
    }

    this.isInitializing = true;

    try {
      // Validar que o elemento existe no DOM
      if (!this.isConnected) {
        return;
      }

      if (this.config.choiceEnabled === true) {
        await this.initializeGiftChoiceCoordinator();
        this.setAttribute('data-initialized', 'true');
        return;
      }

      // Limpar atributos de carrinho antigos que podem estar causando conflitos
      await this.cleanOldCartAttributes();

      // Buscar elementos DOM com nova estrutura
      this.progressWrapper = this.querySelector('[data-gift-progress-wrapper]');
      this.progressBar = this.querySelector('[data-gift-progress]');
      this.progressFill = this.querySelector('[data-gift-progress-meter]');
      this.progressText = this.querySelector('.progress-meter .text');
      this.progressMessage = this.querySelector('[data-gift-message]');
      this.giftProducts = this.querySelector('[data-gift-products]');
      this.giftsContainer = this.querySelector('[data-gifts-list]');

      // Buscar carrinho atual
      await this.fetchCart();

      // Verificar níveis desbloqueados
      this.checkAllLevels();

      // Aplicar cores personalizadas
      this.applyCustomColors();

      // Atualizar UI
      this.updateUI();

      // Anexar event listeners globais (singleton)
      this.attachEventListeners();

      // Marcar como inicializado
      this.setAttribute('data-initialized', 'true');

    } catch (error) {
      // Erro silencioso
    } finally {
      this.isInitializing = false;
    }
  }

  async initializeGiftChoiceCoordinator() {
    this.progressWrapper = this.querySelector('[data-gift-progress-wrapper]');
    this.progressBar = this.querySelector('[data-gift-progress]');
    this.progressFill = this.querySelector('[data-gift-progress-meter]');
    this.progressText = this.querySelector('.progress-meter .text');
    this.progressMessage = this.querySelector('[data-gift-message]');
    this.giftProducts = this.querySelector('[data-gift-products]');
    this.giftsContainer = this.querySelector('[data-gifts-list]');
    this.choiceCoordinator = getGiftChoiceCoordinator(this.runtime);
    this.choiceCoordinator.bindChoiceModal();
    this.choiceCoordinator.bindChoiceSurfaceActions();
    this.choiceCoordinator.installCheckoutGuard();
    this.choiceCoordinator.syncAcceleratedCheckoutAvailability();
    this.choiceCoordinator.registerPresenter(this);

    if (
      this.runtime.document &&
      typeof this.runtime.document.querySelectorAll === 'function'
    ) {
      this.runtime.document
        .querySelectorAll('gift-progress-component')
        .forEach((presenter) => {
          presenter.choiceCoordinator = this.choiceCoordinator;
          this.choiceCoordinator.registerPresenter(presenter);
        });
    }

    if (
      this.choiceCoordinator.cartGateway &&
      typeof this.choiceCoordinator.cartGateway.getCart === 'function'
    ) {
      this.currentCart = await this.choiceCoordinator.cartGateway.getCart();
    } else {
      await this.fetchCart();
    }

    attachGiftChoiceCartListener(this.runtime);
    await this.choiceCoordinator.requestReconciliation({
      source: 'initial',
      cart: this.currentCart,
    });
    this.applyCustomColors();
  }

  disconnectedCallback() {
    if (
      this.config.choiceEnabled === true &&
      this.choiceCoordinator
    ) {
      this.choiceCoordinator.unregisterPresenter(this);
    }
  }

  renderGiftCoordinatorState({
    cart,
    unlockedLevels,
    levels,
  } = {}) {
    if (cart && Array.isArray(cart.items)) {
      this.currentCart = cart;
    }

    this.unlockedLevels = Array.isArray(unlockedLevels)
      ? unlockedLevels
      : (
        Array.isArray(levels)
          ? levels.filter((level) => level.eligible === true)
          : []
      );
    this.updateUI();
  }

  /**
   * Aplica cores personalizadas via CSS variables
   */
  applyCustomColors() {
    if (!this.config || !this.config.colors) {
      return;
    }

    const colors = this.config.colors;
    
    // Aplicar no elemento principal (gift-system-container)
    if (this.style) {
      this.style.setProperty('--gift-progress-color-30', colors.progress30 || '#a78bfa');
      this.style.setProperty('--gift-progress-color-60', colors.progress60 || '#8b5cf6');
      this.style.setProperty('--gift-progress-color-100', colors.progress100 || '#7c3aed');
      this.style.setProperty('--gift-progress-color-unlocked', colors.progressUnlocked || '#7c3aed');
      this.style.setProperty('--gift-progress-bg-color', colors.progressBg || '#f0f0f0');
      this.style.setProperty('--gift-progress-text-color', colors.progressText || '#ffffff');
      this.style.setProperty('--gift-message-color-default', colors.messageDefault || '#666666');
      this.style.setProperty('--gift-message-color-30', colors.message30 || '#a78bfa');
      this.style.setProperty('--gift-message-color-60', colors.message60 || '#8b5cf6');
      this.style.setProperty('--gift-message-color-100', colors.message100 || '#7c3aed');
      this.style.setProperty('--gift-message-color-unlocked', colors.messageUnlocked || '#7c3aed');
    }

    // Aplicar também no wrapper da barra de progresso (haloCalculatorGift)
    if (this.progressWrapper && this.progressWrapper.style) {
      this.progressWrapper.style.setProperty('--gift-progress-color-30', colors.progress30 || '#a78bfa');
      this.progressWrapper.style.setProperty('--gift-progress-color-60', colors.progress60 || '#8b5cf6');
      this.progressWrapper.style.setProperty('--gift-progress-color-100', colors.progress100 || '#7c3aed');
      this.progressWrapper.style.setProperty('--gift-progress-color-unlocked', colors.progressUnlocked || '#7c3aed');
      this.progressWrapper.style.setProperty('--gift-progress-bg-color', colors.progressBg || '#f0f0f0');
      this.progressWrapper.style.setProperty('--gift-progress-text-color', colors.progressText || '#ffffff');
      this.progressWrapper.style.setProperty('--gift-message-color-default', colors.messageDefault || '#666666');
      this.progressWrapper.style.setProperty('--gift-message-color-30', colors.message30 || '#a78bfa');
      this.progressWrapper.style.setProperty('--gift-message-color-60', colors.message60 || '#8b5cf6');
      this.progressWrapper.style.setProperty('--gift-message-color-100', colors.message100 || '#7c3aed');
      this.progressWrapper.style.setProperty('--gift-message-color-unlocked', colors.messageUnlocked || '#7c3aed');
    }
  }

  /**
   * Anexa event listeners (singleton - apenas uma vez globalmente)
   */
  attachEventListeners() {
    // Verificar se já foi anexado globalmente
    if (window.giftSystemListenersAttached) {
      return;
    }


    try {
      // Listener para mudanças no carrinho - usar o evento correto do tema
      document.addEventListener('cart-update', (e) => {
        // Ignorar eventos disparados pelo próprio gift system
        if (e.detail && e.detail.source === 'gift-system') {
          return;
        }
        if (e.detail && e.detail.source === 'gift-trigger-picker' && !Array.isArray(e.detail.items)) {
          return;
        }
        const hasCartPayload = e.detail && Array.isArray(e.detail.items);
        const skipGiftAdd = e.detail && e.detail.source === 'gift-trigger-picker-batch';
        // Atualizar TODOS os gift-system-manager na página
        const managers = document.querySelectorAll('gift-system-manager');
        managers.forEach(manager => {
          if (document.body.classList.contains('cart-sidebar-show') || hasCartPayload || skipGiftAdd) {
            if (manager.handleCartUpdate) {
              manager.handleCartUpdate(hasCartPayload ? e.detail : undefined, { skipGiftAdd });
            }
          } else {
            if (manager.debouncedCartUpdate) {
              manager.debouncedCartUpdate();
            }
          }
        });
      });

      // Listener para free-shipping component (já existe no tema)
      const freeShipping = document.querySelector('free-shipping-component');
      if (freeShipping) {
        freeShipping.addEventListener('change', () => {
          document.querySelectorAll('gift-system-manager').forEach(manager => {
            if (manager.debouncedCartUpdate) {
              manager.debouncedCartUpdate();
            }
          });
        });
      }

      // Marcar como anexado
      window.giftSystemListenersAttached = true;
    } catch (error) {
      // Erro silencioso
    }
  }

  /**
   * Debounced cart update para evitar múltiplas chamadas
   */
  debouncedCartUpdate() {
    if (this.cartUpdateTimeout) {
      clearTimeout(this.cartUpdateTimeout);
    }
    // Reduzir delay quando quick cart está aberto (otimização)
    const delay = document.body.classList.contains('cart-sidebar-show') ? 300 : 1000;
    this.cartUpdateTimeout = setTimeout(() => {
      this.handleCartUpdate();
    }, delay);
  }

  /**
   * Busca carrinho via Shopify API
   */
  async fetchCart() {
    return new Promise((resolve, reject) => {
      if (typeof Shopify === 'undefined' || !Shopify.getCart) {
        // Fallback: fetch direto
        fetch('/cart.js')
          .then(res => res.json())
          .then(cart => {
            this.currentCart = cart;
            resolve(cart);
          })
          .catch(reject);
      } else {
        Shopify.getCart((cart) => {
          this.currentCart = cart;
          resolve(cart);
        });
      }
    });
  }

  /**
   * Handle cart update
   */
  async handleCartUpdate(cartPayload, options) {
    const opts = options || {};
    // Não bloquear - apenas agendar para depois se estiver processando
    if (this.isProcessing && !opts.skipGiftAdd) {
      // Reagendar com delay otimizado baseado no contexto. O reagendamento
      // NÃO reusa o cartPayload capturado no clique original: por
      // definição, algo mais está em andamento agora, então esse
      // instantâneo já pode estar velho (não sabe do que o ciclo em voo
      // vai concluir). Sem payload, o ciclo reagendado busca o carrinho
      // recém-buscado (fetchCart) em vez de decidir com dado velho — sem
      // custo no uso normal, porque só entra aqui quando já havia
      // concorrência (isProcessing true), nunca no primeiro clique.
      const delay = document.body.classList.contains('cart-sidebar-show') ? 120 : 250;
      setTimeout(() => this.handleCartUpdate(undefined, options), delay);
      return;
    }

    if (this.config.choiceEnabled === true) {
      try {
        this.choiceCoordinator = this.choiceCoordinator ||
          getGiftChoiceCoordinator(this.runtime);

        if (cartPayload && Array.isArray(cartPayload.items)) {
          this.currentCart = cartPayload;
        } else if (
          this.choiceCoordinator.cartGateway &&
          typeof this.choiceCoordinator.cartGateway.getCart === 'function'
        ) {
          this.currentCart = await this.choiceCoordinator.cartGateway.getCart();
        } else {
          await this.fetchCart();
        }

        // skipGiftAdd só vale no legado (picker já embutiu o brinde no POST).
        // Com escolha, SEMPRE reconcilia: 2+ opções abrem o modal; 1 auto-add.
        const reconcileSource = (
          opts.skipGiftAdd
            ? 'gift-trigger-picker'
            : 'manager'
        );

        await this.choiceCoordinator.requestReconciliation({
          source: reconcileSource,
          cart: this.currentCart,
        });
        this.updateUI();
      } catch (error) {
        // Falha do ramo novo não devolve ownership ao fluxo legado.
      }

      return;
    }

    try {
      // Salvar estado anterior do carrinho antes de atualizar
      this.previousCart = this.currentCart ? JSON.parse(JSON.stringify(this.currentCart)) : null;

      if (cartPayload && Array.isArray(cartPayload.items)) {
        this.currentCart = cartPayload;
      } else {
        await this.fetchCart();
      }

      // Caminho rápido: picker já adicionou gatilho+brinde no mesmo POST
      if (opts.skipGiftAdd) {
        this.updateUI();
        return;
      }

      // Detectar remoção manual de brindes
      this.detectManualGiftRemoval();

      // Resetar bloqueios se requisitos mudaram
      this.resetBlockedLevelsIfNeeded();

      await this.checkAllLevels();

      await this.removeInvalidGifts();
      await this.convergeGiftLineQuantities();
      this.updateUI();
    } catch (error) {
      // Erro silencioso
    }
  }

  /**
   * Calcula total do carrinho excluindo brindes
   */
  getCartTotalExcludingGifts() {
    if (!this.currentCart || !this.currentCart.items) return 0;

    const totalCents = this.currentCart.items.reduce((total, item) => {
      if (item.properties && item.properties._is_gift === 'true') {
        return total;
      }
      return total + (item.original_line_price || item.line_price);
    }, 0);

    return totalCents / 100;
  }

  /**
   * Verifica todos os níveis de brinde
   */
  async checkAllLevels() {
    this.unlockedLevels = [];

    if (!this.currentCart || !this.currentCart.items) {
      return;
    }

    this.levels.forEach((level, index) => {
      if (!level.enabled && index > 0) {
        return; // Nível 1 está sempre enabled
      }

      const eligible = this.checkLevelEligibility(level);

      if (eligible) {
        // Verificar se já não foi adicionado brinde deste nível
        const alreadyHasGift = this.checkIfLevelGiftAdded(level.levelNumber);

        this.unlockedLevels.push({
          ...level,
          alreadyHasGift
        });
      }
    });

    // Processar brindes desbloqueados (adicionar ou exibir escolha)
    await this.processUnlockedGifts();
  }

  /**
   * Verifica se um nível específico foi desbloqueado
   */
  checkLevelEligibility(level) {
    if (!this.currentCart) {
      return false;
    }

    // Calcular total do carrinho EXCLUINDO brindes
    const cartTotalCents = this.currentCart.items.reduce((total, item) => {
      // Ignorar itens que são brindes
      if (item.properties && item.properties._is_gift === 'true') {
        return total;
      }
      return total + (item.original_line_price || item.line_price);
    }, 0);

    const cartTotal = cartTotalCents / 100; // Converter de centavos para reais
    const minValue = level.minValue;

    // Verificar valor mínimo
    if (cartTotal < minValue) {
      return false;
    }

    // Verificar produtos gatilho (quantidade e modo de contagem vêm do
    // núcleo único, alimentado pela configuração do nível). Sem cópia local
    // da regra: a mesma função decide para motor, barra e card.
    const core = this.runtime.GiftChoiceCore || GiftChoiceCoreDependency;
    const triggerProgress = core.resolveTriggerProgress({
      cartItems: this.currentCart.items,
      triggerProducts: level.triggerProducts,
      requiredCount: level.triggerMinCount,
      countMode: level.triggerCountMode,
    });

    return triggerProgress.satisfied;
  }

  /**
   * Gerencia bloqueios de níveis no localStorage
   */
  getBlockedLevels() {
    try {
      const blocked = localStorage.getItem('gift_system_blocked_levels');
      return blocked ? JSON.parse(blocked) : [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Adiciona um nível à lista de bloqueados
   */
  blockLevel(levelNumber) {
    const blocked = this.getBlockedLevels();
    if (!blocked.includes(levelNumber)) {
      blocked.push(levelNumber);
      localStorage.setItem('gift_system_blocked_levels', JSON.stringify(blocked));
    }
  }

  /**
   * Remove um nível da lista de bloqueados
   */
  unblockLevel(levelNumber) {
    const blocked = this.getBlockedLevels();
    const index = blocked.indexOf(levelNumber);
    if (index > -1) {
      blocked.splice(index, 1);
      localStorage.setItem('gift_system_blocked_levels', JSON.stringify(blocked));
    }
  }

  /**
   * Verifica se um nível está bloqueado
   */
  isLevelBlocked(levelNumber) {
    const blocked = this.getBlockedLevels();
    return blocked.includes(levelNumber);
  }

  /**
   * Limpa todos os bloqueios
   */
  clearAllBlockedLevels() {
    localStorage.removeItem('gift_system_blocked_levels');
  }

  /**
   * Detecta remoção manual de brindes comparando carrinho anterior e atual
   */
  detectManualGiftRemoval() {
    if (!this.previousCart || !this.currentCart) {
      return;
    }

    // Buscar brindes que estavam no carrinho anterior
    const previousGifts = (this.previousCart.items || []).filter(item =>
      item.properties && item.properties._is_gift === 'true'
    );

    // Buscar brindes que estão no carrinho atual
    const currentGifts = (this.currentCart.items || []).filter(item =>
      item.properties && item.properties._is_gift === 'true'
    );

    // Verificar quais brindes foram removidos
    for (const previousGift of previousGifts) {
      const giftStillExists = currentGifts.some(currentGift =>
        currentGift.key === previousGift.key ||
        (currentGift.variant_id === previousGift.variant_id &&
         currentGift.properties._gift_level === previousGift.properties._gift_level)
      );

      if (!giftStillExists) {
        // Brinde foi removido - verificar se foi remoção manual ou automática
        const giftLevel = parseInt(previousGift.properties._gift_level);
        const level = this.levels[giftLevel - 1];
        
        if (!level) continue;

        // Verificar se o nível ainda está elegível com o carrinho atual
        // Se ainda está elegível mas o brinde foi removido, foi remoção manual
        const isStillEligible = this.checkLevelEligibility(level);

        if (isStillEligible) {
          // Nível ainda está elegível mas brinde foi removido = remoção manual
          this.blockLevel(giftLevel);
        }
        // Se não está mais elegível, foi remoção automática (removeInvalidGifts) - não bloquear
      }
    }
  }

  /**
   * Reseta bloqueios quando requisitos do nível mudam
   */
  resetBlockedLevelsIfNeeded() {
    if (!this.currentCart) {
      return;
    }

    const blockedLevels = this.getBlockedLevels();
    if (blockedLevels.length === 0) {
      return;
    }

    // Para cada nível bloqueado, verificar se os requisitos mudaram
    for (const levelNumber of blockedLevels) {
      const level = this.levels[levelNumber - 1];
      if (!level) continue;

      // Verificar se o nível ainda está elegível
      const isEligible = this.checkLevelEligibility(level);

      // Se não está mais elegível, desbloquear (requisitos mudaram)
      if (!isEligible) {
        this.unblockLevel(levelNumber);
      } else {
        // Verificar se havia um brinde anteriormente e ainda existe
        // Se não existe mais e os requisitos continuam os mesmos, manter bloqueado
        const hasGift = this.currentCart.items.some(item =>
          item.properties &&
          item.properties._is_gift === 'true' &&
          item.properties._gift_level == levelNumber
        );

        // Se não tem brinde e requisitos continuam atendidos, manter bloqueado
        // (foi removido manualmente e ainda pode ser readicionado automaticamente)
        // Mas vamos comparar com o carrinho anterior para ver se requisitos mudaram
        
        if (this.previousCart) {
          const previousTotal = this.getCartTotalExcludingGiftsFromCart(this.previousCart);
          const currentTotal = this.getCartTotalExcludingGifts();
          
          // Se o valor total mudou significativamente (mais de 1% de diferença), pode resetar
          const totalChanged = Math.abs(previousTotal - currentTotal) > (level.minValue * 0.01);
          
          // Verificar se produtos gatilho mudaram
          const triggerProductsChanged = this.checkTriggerProductsChanged(level);
          
          if (totalChanged || triggerProductsChanged) {
            // Requisitos mudaram de alguma forma, desbloquear
            this.unblockLevel(levelNumber);
          }
        }
      }
    }
  }

  /**
   * Calcula total do carrinho excluindo brindes (versão com carrinho como parâmetro)
   */
  getCartTotalExcludingGiftsFromCart(cart) {
    if (!cart || !cart.items) return 0;

    const totalCents = cart.items.reduce((total, item) => {
      if (item.properties && item.properties._is_gift === 'true') {
        return total;
      }
      return total + (item.original_line_price || item.line_price);
    }, 0);

    return totalCents / 100;
  }

  /**
   * Verifica se produtos gatilho mudaram entre carrinho anterior e atual.
   * Delega ao núcleo (mesma função que checkLevelEligibility e
   * hasTriggerProductInCart chamam): o que importa é a mudança na
   * quantidade atingida de gatilhos (missing), não só presença. Com
   * triggerMinCount 1 (toda campanha no ar hoje), missing só assume 0 ou 1,
   * reproduzindo bit a bit a checagem de presença que existia antes.
   */
  checkTriggerProductsChanged(level) {
    if (!level.triggerProducts || level.triggerProducts.length === 0) {
      return false; // Sem produtos gatilho, não há mudança
    }

    if (!this.previousCart || !this.currentCart) {
      return false;
    }

    const core = this.runtime.GiftChoiceCore || GiftChoiceCoreDependency;

    const progressBefore = core.resolveTriggerProgress({
      cartItems: this.previousCart.items,
      triggerProducts: level.triggerProducts,
      requiredCount: level.triggerMinCount,
      countMode: level.triggerCountMode,
    });

    const progressNow = core.resolveTriggerProgress({
      cartItems: this.currentCart.items,
      triggerProducts: level.triggerProducts,
      requiredCount: level.triggerMinCount,
      countMode: level.triggerCountMode,
    });

    // Se a quantidade que falta mudou (inclui virar 0 ou deixar de ser 0)
    return progressBefore.missing !== progressNow.missing;
  }

  /**
   * Processa brindes desbloqueados
   */
  async processUnlockedGifts() {

    if (this.isProcessing) {
      return;
    }


    let hasGiftsAlready = false;
    let needsToAddGift = false;

    for (const level of this.unlockedLevels) {

      if (level.alreadyHasGift) {
        hasGiftsAlready = true;
        continue; // Já tem brinde deste nível
      }

      // Verificar se o nível está bloqueado (brinde foi removido manualmente)
      if (this.isLevelBlocked(level.levelNumber)) {
        continue; // Não adicionar brinde se nível está bloqueado
      }

      const availableGifts = level.availableGifts || [];

      if (availableGifts.length > 0) {
      }

      if (!availableGifts || availableGifts.length === 0) {
        continue;
      }

      needsToAddGift = true;

      // SEMPRE adiciona primeiro brinde automaticamente (sem interface de seleção)
      if (availableGifts && availableGifts.length > 0) {
        await this.addGiftAutomatically(availableGifts[0], level);
      }
    }

    // Se já tinha brindes mas não adicionou novos, forçar refresh para sincronizar UI
    if (hasGiftsAlready && !needsToAddGift && document.body.classList.contains('cart-sidebar-show')) {

      // Refresh com delay de 1 segundo para dar tempo do tema processar
      setTimeout(() => {

        // Recarregar HTML do quick cart via AJAX (igual o tema faz)
        if (typeof $ !== 'undefined' && window.routes && window.routes.root !== undefined) {
          const $cartDropdown = $('#halo-cart-sidebar .halo-sidebar-wrapper .previewCart-wrapper');

          $.ajax({
            type: 'GET',
            url: window.routes.root + '/cart?view=ajax_side_cart',
            cache: false,
            success: function (data) {
              const response = $(data);
              $cartDropdown.html(response);
            },
            error: function () {
            }
          });
        } else {
        }
      }, 1000);
    }
  }

  /**
   * Adiciona brinde automaticamente (quando há apenas 1 opção)
   */
  async addGiftAutomatically(productHandleOrObj, level) {
    // Serialização por nível (CART-07): enquanto uma adição já está em voo
    // (para qualquer nível — processUnlockedGifts processa um nível por
    // vez, sempre esperando o anterior terminar), outra tentativa é
    // descartada aqui, na hora, e não reagendada como o handleCartUpdate
    // externo faz. Como só existe um fluxo de adição por instância e ele
    // nunca roda em paralelo consigo mesmo, isProcessing já é, na prática,
    // a serialização por nível: nunca há dois níveis com adição em voo ao
    // mesmo tempo nesta instância.
    if (this.isProcessing) {
      return;
    }

    this.isProcessing = true;

    try {
      // Extrair handle do produto (pode vir como string ou objeto)
      const productHandle = typeof productHandleOrObj === 'string'
        ? productHandleOrObj
        : (productHandleOrObj?.handle || productHandleOrObj);


      // Buscar dados do produto
      const product = await this.fetchProductByHandle(productHandle);

      if (!product || !product.variants || product.variants.length === 0) {
        throw new Error('Produto de brinde não encontrado');
      }

      const variant = product.variants[0];

      // Verificação: Este produto já está no carrinho COMO BRINDE?
      const giftAlreadyInCart = this.currentCart.items.some(item =>
        (item.handle === product.handle || item.variant_id === variant.id) &&
        item.properties && item.properties._is_gift === 'true'
      );

      if (giftAlreadyInCart) {
        return; // SAIR SEM ADICIONAR BRINDE
      }

      // Verificar se produto normal existe (apenas para log informativo)
      const regularProductInCart = this.currentCart.items.some(item =>
        (item.handle === product.handle || item.variant_id === variant.id) &&
        (!item.properties || item.properties._is_gift !== 'true')
      );

      if (regularProductInCart) {
      }

      // Verificar estoque
      if (!variant.available) {
        this.showError(window.giftSystemTranslations?.errors?.out_of_stock || 'Brinde indisponível no momento');
        return;
      }

      // Adicionar ao carrinho com propriedades especiais
      const properties = {
        '_is_gift': 'true',
        '_gift_level': level.levelNumber.toString(),
        '_gift_discount_percent': level.discountPercent.toString(),
        '_original_price': variant.price.toString()
      };

      // Otimização: addToCart já atualiza o carrinho e dispara eventos
      // Passar o carrinho atualizado para refreshCart para evitar busca redundante
      await this.addToCart(variant.id, 1, properties);
      
      // REMOVIDO: applyGiftDiscount causava conflito ao aplicar desconto em todos os produtos com mesmo variant_id
      // O desconto já é aplicado via properties do line item (_gift_discount_percent)

      // Atualizar tracking
      this.updateAnalytics('gift_auto_added', {
        level: level.levelNumber,
        product_id: product.id,
        variant_id: variant.id
      });

      // Otimização: refreshCart recebe carrinho já atualizado (currentCart foi atualizado em addToCart)
      // Apenas atualizar UI se necessário, sem buscar carrinho novamente
      // Se addToCart já disparou evento cart-update, o tema já vai atualizar automaticamente
      // Mas ainda precisamos atualizar a UI do quick cart se estiver aberto
      if (document.body.classList.contains('cart-sidebar-show')) {
        // Apenas atualizar quick cart, sem buscar carrinho novamente
        await this.refreshCart(this.currentCart);
      }

      // Mostrar popup de sucesso (se configurado)
      if (this.config.showPopup) {
        this.showSuccessPopup(product, 'single');
      }

    } catch (error) {
      this.showError(window.giftSystemTranslations?.errors?.failed_to_add || 'Erro ao adicionar brinde');
    } finally {
      this.isProcessing = false;
    }
  }

  async addToCart(variantId, quantity, properties) {
    const formData = {
      items: [{
        id: variantId,
        quantity: quantity,
        properties: properties
      }]
    };

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(formData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.description || 'Erro ao adicionar ao carrinho');
    }

    const result = await response.json();

    // Otimização: Usar Shopify.getCart para atualizar estado e disparar evento
    // Elimina a necessidade de fetchCart() separado
    return new Promise((resolve, reject) => {
      if (typeof Shopify !== 'undefined' && Shopify.getCart) {
        Shopify.getCart((cart) => {
          // Atualizar carrinho internamente usando resultado de Shopify.getCart
          this.currentCart = cart;
          
          // Disparar evento de atualização para que o tema atualize a UI
          document.dispatchEvent(new CustomEvent('cart-update', {
            detail: cart
          }));
          
          resolve(result);
        });
      } else {
        // Fallback: buscar carrinho se Shopify.getCart não estiver disponível
        this.fetchCart().then(() => {
          resolve(result);
        }).catch(reject);
      }
    });
  }

  /**
   * Atualiza o carrinho e dispara eventos para UI
   * @param {Object} cart - Carrinho opcional (se já carregado, evita requisição redundante)
   */

  /**
   * Substitui o HTML principal do carrinho sem reload completo.
   */
  async softRefreshCartPage() {
    try {
      const res = await fetch(window.location.pathname + window.location.search, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        cache: 'no-store'
      });
      if (!res.ok) return false;
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const selectors = [
        '.cart-container',
        '.main-cart',
        'form[action="/cart"]',
        'form.cart',
        '#MainContent .cart'
      ];
      for (let i = 0; i < selectors.length; i++) {
        const neu = doc.querySelector(selectors[i]);
        const old = document.querySelector(selectors[i]);
        if (neu && old) {
          old.replaceWith(neu);
          return true;
        }
      }
      return false;
    } catch (e) {
      return false;
    }
  }

  async refreshCart(cart = null) {
    try {
      // Otimização: Se carrinho já foi passado, usar diretamente ao invés de buscar novamente
      if (cart) {
        this.currentCart = cart;
      } else {
        // Atualizar carrinho internamente apenas se não foi fornecido
        await this.fetchCart();
      }

      // /cart: soft refresh (sem location.reload — maior fonte de lag)
      if (window.location.pathname === '/cart' || window.location.pathname.endsWith('/cart')) {
        const ok = await this.softRefreshCartPage();
        if (!ok) {
          window.location.reload();
        }
        return;
      }

      // Se quick cart está aberto, recarregar via AJAX
      if (document.body.classList.contains('cart-sidebar-show')) {
        if (typeof $ !== 'undefined' && window.routes && window.routes.root !== undefined) {
          const $cartDropdown = $('#halo-cart-sidebar .halo-sidebar-wrapper .previewCart-wrapper');

          $.ajax({
            type: 'GET',
            url: window.routes.root + '/cart?view=ajax_side_cart',
            cache: false,
            success: function (data) {
              const response = $(data);
              $cartDropdown.html(response);
            }
          });
        }
      }

    } catch (error) {
    }
  }

  /**
   * Limpa atributos de carrinho antigos que podem estar causando conflitos
   */
  async cleanOldCartAttributes() {
    try {
      // Buscar o carrinho atual
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      // Verificar se existem atributos antigos do tipo _gift_{variant_id}_discount
      if (cart.attributes) {
        const attributesToRemove = {};
        let hasOldAttributes = false;

        // Procurar por atributos antigos de desconto
        for (const key in cart.attributes) {
          if (key.startsWith('_gift_') && key.endsWith('_discount')) {
            attributesToRemove[key] = ''; // String vazia remove o atributo
            hasOldAttributes = true;
          }
        }

        // Se encontrou atributos antigos, removê-los
        if (hasOldAttributes) {

          const response = await fetch('/cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              attributes: attributesToRemove
            })
          });

          if (response.ok) {
          }
        }
      }
    } catch (error) {
      // Erro silencioso
    }
  }

  /**
   * Aplica desconto no brinde via cart attributes
   */
  async applyGiftDiscount(variantId, discountPercent) {
    const attributeKey = `_gift_${variantId}_discount`;

    const updates = {
      attributes: {
        [attributeKey]: discountPercent.toString()
      }
    };


    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        // Erro silencioso
      }


      // Se desconto for 100%, tentar aplicar código de desconto automático
      if (discountPercent === 100) {
        const discountCode = `GIFT-${variantId}`;
        localStorage.setItem('storedDiscount', discountCode);
      }

    } catch (error) {
      // Erro silencioso
    }
  }

  /**
   * Busca produto por handle (com cache para otimização)
   */
  async fetchProductByHandle(handle) {
    // Remover 'gid://shopify/Product/' se vier do product_list
    const cleanHandle = handle.toString().replace('gid://shopify/Product/', '');

    // Otimização: Verificar cache primeiro
    if (this.productCache.has(cleanHandle)) {
      return this.productCache.get(cleanHandle);
    }

    let product = null;

    // Se for ID numérico, buscar por ID
    if (!isNaN(cleanHandle)) {
      // Converter ID para handle via metafield ou busca
      // Por enquanto, tentar buscar direto
      const response = await fetch(`/products/${cleanHandle}.js`);
      if (response.ok) {
        product = await response.json();
      }
    }

    // Se não encontrou por ID ou não é ID numérico, buscar por handle
    if (!product) {
      const response = await fetch(`/products/${cleanHandle}.js`);

      if (!response.ok) {
        throw new Error(`Produto não encontrado: ${cleanHandle}`);
      }

      product = await response.json();
    }

    // Armazenar no cache para próximas chamadas
    if (product) {
      this.productCache.set(cleanHandle, product);
    }

    return product;
  }

  /**
   * Verifica se já tem brinde de determinado nível no carrinho
   */
  checkIfLevelGiftAdded(levelNumber) {
    if (!this.currentCart || !this.currentCart.items) {
      return false;
    }

    return this.currentCart.items.some(item =>
      item.properties &&
      item.properties._is_gift === 'true' &&
      item.properties._gift_level == levelNumber
    );
  }

  /**
   * Remove brindes inválidos (quando regras não são mais atendidas)
   */
  async removeInvalidGifts() {
    if (!this.currentCart || !this.currentCart.items) {
      return;
    }

    const giftsInCart = this.currentCart.items.filter(item =>
      item.properties && item.properties._is_gift === 'true'
    );

    let giftRemoved = false;

    for (const gift of giftsInCart) {
      const giftLevel = parseInt(gift.properties._gift_level);
      const levelStillUnlocked = this.unlockedLevels.some(
        level => level.levelNumber === giftLevel
      );

      if (!levelStillUnlocked) {
        // Remover brinde
        try {

          await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              id: gift.key,
              quantity: 0
            })
          });

          giftRemoved = true;

          // Analytics
          this.updateAnalytics('gift_removed', {
            level: giftLevel,
            reason: 'level_no_longer_unlocked'
          });

        } catch (error) {
          // Erro silencioso
        }
      }
    }

    // Se removeu algum brinde, atualizar o carrinho na UI
    if (giftRemoved) {
      await this.refreshCart();
    }
  }

  /**
   * Garante, para cada nível configurado, a mesma invariante que o
   * caminho do coordenador já garante (convergeLevelToSingleGift): no
   * máximo uma linha de brinde por nível, com exatamente uma unidade. A
   * decisão (o que remover, o que corrigir) vem do núcleo único
   * (core.planGiftLineConvergence) — o caminho legado só executa. Cobre o
   * caso de uma corrida escapar da serialização por nível: mesmo assim o
   * cliente nunca fica com brinde a mais. Idempotente: carrinho já
   * convergido não dispara nenhuma chamada de alteração.
   */
  async convergeGiftLineQuantities() {
    if (!this.currentCart || !this.currentCart.items) {
      return;
    }

    const core = this.runtime.GiftChoiceCore || GiftChoiceCoreDependency;
    let changed = false;

    for (const level of this.levels) {
      const lines = getGiftChoiceLinesForLevel(this.currentCart, level.levelNumber);

      if (lines.length === 0) {
        continue;
      }

      const plan = core.planGiftLineConvergence({ lines });

      if (plan.alreadyConverged) {
        continue;
      }

      try {
        for (const lineKey of plan.removeLineKeys) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              id: lineKey,
              quantity: 0
            })
          });
        }

        if (plan.quantityFix) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              id: plan.quantityFix.lineKey,
              quantity: plan.quantityFix.quantity
            })
          });
        }

        changed = true;

        this.updateAnalytics('gift_quantity_converged', {
          level: level.levelNumber,
          removed: plan.removeLineKeys.length,
          quantityFixed: Boolean(plan.quantityFix)
        });
      } catch (error) {
        // Erro silencioso
      }
    }

    if (changed) {
      await this.refreshCart();
    }
  }

  /**
   * Atualiza toda a UI
   */
  updateUI() {
    // Reaplicar cores personalizadas (caso tenham mudado)
    this.applyCustomColors();
    this.updateProgressBar();
  }

  /**
   * Atualiza barra de progresso
   */

  /**
   * Progresso completo do gatilho (required/matched/missing/satisfied), do
   * núcleo único (mesma função que checkLevelEligibility já chama). Alimenta
   * a elegibilidade, o preenchimento proporcional da barra e a mensagem de
   * quantidade faltante — sem cópia local da regra de contagem.
   */
  resolveTriggerProgress(level, cart) {
    const core = this.runtime.GiftChoiceCore || GiftChoiceCoreDependency;

    return core.resolveTriggerProgress({
      cartItems: cart && cart.items,
      triggerProducts: level && level.triggerProducts,
      requiredCount: level && level.triggerMinCount,
      countMode: level && level.triggerCountMode,
    });
  }

  hasTriggerProductInCart(level, cart) {
    return this.resolveTriggerProgress(level, cart).satisfied;
  }

  /**
   * Nível atual/próximo/alvo a partir dos níveis configurados e do total
   * elegível do carrinho, do núcleo único — a mesma função que a barra do
   * quick cart, o card e o botão de produtos chamam. Sem cópia local do
   * critério de ordenação/seleção.
   */
  resolveTargetLevel(levels, cartTotal) {
    const core = this.runtime.GiftChoiceCore || GiftChoiceCoreDependency;

    return core.resolveTargetLevel({ levels, cartTotal });
  }

  applyProgressUI(progress, message, progressClass) {
    const safeProgress = Math.min(100, Math.max(0, Math.round(progress)));
    this.progressFill.style.width = `${safeProgress}%`;
    if (this.progressText) {
      this.progressText.textContent = `${safeProgress}%`;
    }
    this.progressMessage.textContent = message;
    this.setProgressClasses(progressClass);
    this.syncTriggerPickerButton();
  }
  syncTriggerPickerButton() {
    if (window.GiftTriggerPicker && typeof window.GiftTriggerPicker.syncButtons === 'function') {
      window.GiftTriggerPicker.syncButtons(this.currentCart);
      return;
    }

    const wrap = this.querySelector
      ? this.querySelector('[data-gift-trigger-picker-actions]')
      : null;
    const rootWrap = document.querySelector('.gift-system-container [data-gift-trigger-picker-actions]');
    const target = wrap || rootWrap;
    if (!target) return;

    const cfg = this.config || window.giftSystemConfig;
    if (!cfg || !cfg.enabled || (cfg.triggerPicker && cfg.triggerPicker.enabled === false)) {
      target.hidden = true;
      return;
    }

    // Fallback local se o script do picker ainda não carregou. Mesmo nível
    // que a barra está mostrando, do núcleo único — não mais o de menor
    // valor mínimo.
    const { targetLevel: level } = this.resolveTargetLevel(cfg.levels, this.getCartTotalExcludingGifts());
    if (!level || !level.triggerProducts || !level.triggerProducts.length || !this.currentCart) {
      target.hidden = true;
      return;
    }
    const unlocked = this.checkLevelEligibility(level);
    target.hidden = unlocked;
  }
  progressClassForPercent(progress, fullyUnlocked) {
    if (fullyUnlocked) {
      return 'progress-free';
    }
    if (progress >= 100) {
      return 'progress-100';
    }
    if (progress >= 60) {
      return 'progress-60';
    }
    return 'progress-30';
  }
  updateProgressBar() {
    // Re-bind se o DOM foi re-renderizado (soft refresh / AJAX)
    if (!this.progressFill || !this.progressMessage) {
      this.progressWrapper = this.querySelector('[data-gift-progress-wrapper]');
      this.progressBar = this.querySelector('[data-gift-progress]');
      this.progressFill = this.querySelector('[data-gift-progress-meter]');
      this.progressText = this.querySelector('.progress-meter .text');
      this.progressMessage = this.querySelector('[data-gift-message]');
    }

    if (!this.progressFill || !this.progressMessage) {
      return;
    }

    if (!this.currentCart) {
      this.progressFill.style.width = '0%';
      if (this.progressText) this.progressText.textContent = '0%';
      return;
    }

    // Calcular total do carrinho EXCLUINDO brindes
    const cartTotalCents = this.currentCart.items.reduce((total, item) => {
      // Ignorar itens que são brindes
      if (item.properties && item.properties._is_gift === 'true') {
        return total;
      }
      return total + (item.original_line_price || item.line_price);
    }, 0);

    const cartTotal = cartTotalCents / 100;

    // Nível atual/próximo do núcleo único (assets/gift-choice-core.js) —
    // mesma ordenação por valor mínimo que a barra do quick cart, o card e
    // o botão de produtos usam. Quando currentLevel (lastLevel) existe,
    // cartTotal >= lastLevel.minValue sempre é verdade (garantido pela
    // seleção do núcleo).
    const { currentLevel: lastLevel, nextLevel } = this.resolveTargetLevel(this.levels, cartTotal);

    if (!nextLevel) {
      if (lastLevel) {
        const needsTrigger = lastLevel.triggerProducts && lastLevel.triggerProducts.length > 0;
        const triggerProgress = this.resolveTriggerProgress(lastLevel, this.currentCart);
        const isFullyUnlocked = this.checkLevelEligibility(lastLevel);
        const progress = computeGiftProgressPercent(cartTotal, lastLevel.minValue, {
          needsTrigger,
          triggerProgress
        });

        if (isFullyUnlocked) {
          this.applyProgressUI(
            100,
            resolveGiftProgressMessage('unlocked', this.config, {
              remaining: 0,
              levelNumber: lastLevel.levelNumber,
              formatCurrency: (v) => this.formatCurrency(v)
            }),
            'progress-free'
          );
        } else {
          // Valor OK, falta gatilho: fill proporcional (<100%), copy de
          // PRODUTO com a quantidade que falta (não de R$)
          this.applyProgressUI(
            progress,
            resolveGiftProgressMessage('trigger_missing', this.config, {
              remaining: 0,
              missing: triggerProgress.missing,
              levelNumber: lastLevel.levelNumber,
              formatCurrency: (v) => this.formatCurrency(v)
            }),
            'progress-100'
          );
        }
      } else {
        this.applyProgressUI(
          100,
          resolveGiftProgressMessage('unlocked', this.config, {
            remaining: 0,
            formatCurrency: (v) => this.formatCurrency(v)
          }) || 'Todos os brindes desbloqueados!',
          'progress-free'
        );
      }
      return;
    }

    // nextLevel existe => cartTotal < minValue deste nível (ramo "falta valor")
    const needsTrigger = nextLevel.triggerProducts && nextLevel.triggerProducts.length > 0;
    const triggerProgress = this.resolveTriggerProgress(nextLevel, this.currentCart);
    const progress = computeGiftProgressPercent(cartTotal, nextLevel.minValue, {
      needsTrigger,
      triggerProgress
    });
    const remaining = Math.max(nextLevel.minValue - cartTotal, 0);

    // Ainda falta valor: mensagem de progresso com {valor}
    // (mesmo com gatilho no carrinho, o brinde só libera com valor + gatilho)
    this.applyProgressUI(
      progress,
      resolveGiftProgressMessage('progress', this.config, {
        remaining,
        missing: triggerProgress.missing,
        levelNumber: nextLevel.levelNumber,
        formatCurrency: (v) => this.formatCurrency(v)
      }),
      this.progressClassForPercent(progress, false)
    );
  }

  /**
   * Define as classes de estado da barra de progresso
   */
  setProgressClasses(className) {
    // Remover classes antigas
    const classes = ['progress-free', 'progress-30', 'progress-60', 'progress-100'];

    if (this.progressBar) {
      classes.forEach(cls => this.progressBar.classList.remove(cls));
      this.progressBar.classList.add(className);
    }

    if (this.progressMessage) {
      classes.forEach(cls => this.progressMessage.classList.remove(cls));
      this.progressMessage.classList.add(className);
    }
  }

  /**
   * Mostra popup de sucesso
   */
  showSuccessPopup(product, type) {
    return; // Popup desabilitado, agora usamos a barra de progresso

    const popup = document.querySelector('[data-gift-success-popup]');

    if (!popup) {
      return;
    }


    const message = type === 'single'
      ? (this.config.messageUnlockedSingle || '🎁 Parabéns! Seu brinde foi adicionado!')
      : (this.config.messageUnlockedMultiple || '🎉 Brinde selecionado!');

    const messageEl = popup.querySelector('[data-popup-message]');
    const titleEl = popup.querySelector('[data-popup-product-title]');
    const imageEl = popup.querySelector('[data-popup-product-image]');


    if (messageEl) {
      messageEl.textContent = message;
    }

    if (titleEl) {
      titleEl.textContent = product.title;
    } else {
      // Se titleEl não existe, criar ele
      const productContainer = popup.querySelector('.gift-success-product');
      if (productContainer) {
        const newTitleEl = document.createElement('h3');
        newTitleEl.className = 'gift-success-product-title';
        newTitleEl.setAttribute('data-popup-product-title', '');
        newTitleEl.textContent = product.title;
        productContainer.appendChild(newTitleEl);
      }
    }

    if (imageEl) {
      imageEl.src = product.featured_image || (product.images && product.images[0]) || '';
      imageEl.alt = product.title;
      // Garantir que a imagem respeite o CSS
      imageEl.style.cssText = '';
    }


    // Forçar exibição do popup (ModalDialog pode não estar registrado)
    popup.setAttribute('open', '');

    // Forçar display inline também via style para garantir visibilidade
    popup.style.display = 'flex';
    popup.style.alignItems = 'center';
    popup.style.justifyContent = 'center';
    popup.style.position = 'fixed';
    popup.style.top = '0';
    popup.style.left = '0';
    popup.style.width = '100%';
    popup.style.height = '100%';
    popup.style.zIndex = '99999';

    // Forçar background no overlay e conteúdo
    const overlay = popup.querySelector('.gift-popup-overlay');
    const content = popup.querySelector('.gift-popup-content');
    const innerContent = popup.querySelector('.gift-success-content');

    if (overlay) {
      overlay.style.display = 'block';
    }

    if (content) {
      // Forçar apenas display e z-index, deixar CSS cuidar do resto
      content.style.cssText = 'display: block !important; position: relative !important; z-index: 100000 !important;';
    }

    if (innerContent) {
      innerContent.style.display = 'block';
      innerContent.style.visibility = 'visible';
    }

    // Adicionar overflow-hidden no body para bloquear scroll
    document.body.classList.add('overflow-hidden');


    // Confete (se configurado)
    if (this.config.popupStyle === 'celebration') {
      this.triggerConfetti();
    }

    // Fechar automaticamente após 5s
    setTimeout(() => {
      popup.removeAttribute('open');
      popup.style.display = 'none';
      document.body.classList.remove('overflow-hidden');
    }, 5000);
  }

  /**
   * Trigger confete animation
   */
  triggerConfetti() {
    // Implementação simples de confete
    // Se biblioteca canvas-confetti estiver disponível
    if (typeof confetti === 'function') {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
    }
  }

  /**
   * Mostra mensagem de erro
   */
  showError(message) {
    // Mostrar em um toast/alert simples
    // Pode ser melhorado com um componente de notificação
    if (window.alert) {
      alert(message);
    }
  }

  /**
   * Atualiza analytics/tracking
   */
  updateAnalytics(eventType, data) {
    if (!this.config.enableAnalytics) {
      return;
    }

    const tracking = {
      timestamp: new Date().toISOString(),
      event: eventType,
      ...data
    };

    // Salvar em cart attributes
    const currentTracking = this.currentCart.attributes && this.currentCart.attributes._gift_tracking
      ? JSON.parse(this.currentCart.attributes._gift_tracking)
      : [];

    currentTracking.push(tracking);

    fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        attributes: {
          _gift_tracking: JSON.stringify(currentTracking)
        }
      })
    }).catch(error => {
      // Erro silencioso
    });

    // Google Analytics (se disponível)
    if (typeof gtag === 'function') {
      gtag('event', eventType, {
        event_category: 'Gift System',
        ...data
      });
    }

    // Facebook Pixel (se disponível)
    if (typeof fbq === 'function') {
      fbq('trackCustom', 'GiftSystem_' + eventType, data);
    }
  }

  /**
   * Formata valor em moeda BRL
   */
  formatCurrency(value) {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }
}

// Registrar Custom Element
if (
  typeof customElements !== 'undefined' &&
  !customElements.get('gift-system-manager')
) {
  customElements.define('gift-system-manager', GiftSystemManager);
}

// Auto-inicializar após mudanças no carrinho
if (
  typeof document !== 'undefined' &&
  GiftSystemRuntime.giftSystemDomReadyListenerAttached !== true
) {
  GiftSystemRuntime.giftSystemDomReadyListenerAttached = true;
  document.addEventListener('DOMContentLoaded', () => {
    const giftSystem = document.querySelector('gift-system-manager');

    if (giftSystem) {
      // Re-inicializar após mudanças no carrinho
      document.addEventListener('cart:updated', () => {
        if (!giftSystem.isInitializing && !giftSystem.isProcessing) {
          giftSystem.handleCartUpdate();
        }
      });
    }
  });
}

if (typeof module === 'object' && module.exports) {
  module.exports = {
    GiftCartGateway,
    GiftCartMutator,
    GiftChoiceCoordinator,
    GiftDecisionStorageAdapter,
    GiftProductResolver,
    GiftSystemManager,
    attachGiftChoiceCartListener,
    createGiftCartEventDetail,
    getGiftChoiceCoordinator,
    normalizeCartUpdateDetail,
    normalizeGiftChoiceCartUpdate,
    routeGiftChoiceCartUpdate,
  };
}
})();
